import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./TalentHiveAdmin.css";
import { AccessControlPage, DatabasePage } from "./AdminAccess/AdminAcccess";
import RealtimeDashboard from "./Dashboard/RealtimeDashboard";
const API = "/api/admin";
const HRTALK_ADMIN_API = "/api/hrtalk/admin";

/* ══════════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════════ */
const getToken = () => localStorage.getItem("th_token") || localStorage.getItem("token") || "";
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });
const fmt = n => Number(n || 0).toLocaleString("en-IN");
const fmtMoney = n => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtDate = d => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };
const ini = s => (s || "?").split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2);
const avColor = s => { const C = ["#1d240e", "#263012", "#16a34a", "#263012", "#7c3aed", "#0284c7", "#db2777", "#263012"]; let h = 0; for (let i = 0; i < (s || "").length; i++) h = s.charCodeAt(i) + ((h << 5) - h); return C[Math.abs(h) % C.length]; };

const PLAN_COLORS = { job: "#1d240e", cv: "#263012", salary: "#16a34a", career: "#7c3aed" };
const PLAN_ICONS = { job: "🏢", cv: "📄", salary: "💰", career: "🎯" };
const TIER_LABELS = { value: "Value", maxx: "Maxx", supermaxx: "SuperMaxx" };
const planColor = t => PLAN_COLORS[t] || "#8f96a3";
const planIcon = t => PLAN_ICONS[t] || "📦";
const tierLabel = t => TIER_LABELS[t] || t || "—";
const safeStr = (value) => {
  if (Array.isArray(value)) return value.map(item => String(item || "").trim()).filter(Boolean).join(", ");
  if (value == null) return "";
  return String(value);
};
const parseListValue = (value) => {
  if (Array.isArray(value)) return value.map(item => String(item || "").trim()).filter(Boolean);
  const text = safeStr(value).trim();
  if (!text) return [];
  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed.map(item => String(item || "").trim()).filter(Boolean);
    } catch { }
  }
  return text.split(",").map(item => item.trim()).filter(Boolean);
};

const PLAN_META = {
  job: { label: "Job Posting", tiers: { value: { price: 13001, jobLimit: 2 }, maxx: { price: 30000, jobLimit: 4 }, supermaxx: { price: 30010, jobLimit: 8 } } },
  cv: { label: "CV Access", tiers: { value: { price: 13001, cvLimit: 10 }, maxx: { price: 30000, cvLimit: 25 }, supermaxx: { price: 30010, cvLimit: 50 } } },
  salary: { label: "Salary Data", tiers: { value: { price: 13001 }, maxx: { price: 30000 }, supermaxx: { price: 30010 } } },
  career: { label: "Career+", tiers: { value: { price: 13001, interviewLimit: 5 }, maxx: { price: 30000, interviewLimit: 8 }, supermaxx: { price: 30010, interviewLimit: 15 } } },
};

/* ══════════════════════════════════════════════════════════
   MICRO-COMPONENTS
══════════════════════════════════════════════════════════ */

function Toast({ msg, type, onClose }) {
  useEffect(() => { if (!msg) return; const t = setTimeout(onClose, 3800); return () => clearTimeout(t); }, [msg]);
  if (!msg) return null;
  return (
    <div className={`toast ${type === "error" ? "toast-error" : "toast-success"}`}>
      <span style={{ fontSize: 18 }}>{type === "error" ? "⚠️" : "✅"}</span>
      {msg}
    </div>
  );
}

function Av({ name, size = 36, letter }) {
  const bg = avColor(name || letter || "");
  const txt = letter || ini(name);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: size * 0.37, flexShrink: 0, letterSpacing: -0.5 }}>
      {txt}
    </div>
  );
}

function Chip({ label, bg, color, style = {} }) {
  return <span style={{ background: bg, color, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 800, whiteSpace: "nowrap", ...style }}>{label}</span>;
}

function StatusChip({ status }) {
  const MAP = {
    active: { bg: "#dcfce7", color: "#16a34a" },
    pending: { bg: "#fef3c7", color: "#263012" },
    blocked: { bg: "#fee2e2", color: "#263012" },
    open: { bg: "#dcfce7", color: "#16a34a" },
    closed: { bg: "#fee2e2", color: "#263012" },
    draft: { bg: "#fef3c7", color: "#263012" },
    paid: { bg: "#dcfce7", color: "#16a34a" },
    unpaid: { bg: "#fee2e2", color: "#263012" },
    shortlisted: { bg: "#dbeafe", color: "#2563eb" },
    rejected: { bg: "#fee2e2", color: "#263012" },
    applied: { bg: "#ede9fe", color: "#7c3aed" },
    fresher: { bg: "#dbeafe", color: "#2563eb" },
    experienced: { bg: "#ede9fe", color: "#7c3aed" },
    in_review: { bg: "#dbeafe", color: "#2563eb" },
    resolved: { bg: "#dcfce7", color: "#16a34a" },
    free: { bg: "#dcfce7", color: "#16a34a" },
    scheduled: { bg: "#dbeafe", color: "#2563eb" },
    completed: { bg: "#dcfce7", color: "#16a34a" },
    inactive: { bg: "#f3f4f6", color: "#6b7280" },
    intro: { bg: "#dcfce7", color: "#16a34a" },
  };
  const s = MAP[(status || "").toLowerCase()] || { bg: "#f0f2f6", color: "#8f96a3" };
  return <Chip label={status || "—"} bg={s.bg} color={s.color} />;
}

function StatCard({ icon, label, value, sub, color = "#1d240e", trend }) {
  return (
    <div className="sc" style={{ position: "relative", overflow: "hidden", border: "1.5px solid var(--border-light)", transition: "transform 0.2s, box-shadow 0.2s" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{icon}</div>
        {trend != null && <span style={{ fontSize: 11.5, fontWeight: 800, padding: "4px 10px", borderRadius: 99, background: trend >= 0 ? "#dcfce7" : "#fee2e2", color: trend >= 0 ? "#15803d" : "#b91c1c", display: "inline-flex", alignItems: "center", gap: 4 }}>{trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%</span>}
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, color: "var(--text-1)", lineHeight: 1.1, marginBottom: 6, letterSpacing: "-0.5px" }}>{value}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.8px", opacity: 0.85 }}>{label}</div>
      {sub && <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 8, display: "flex", alignItems: "center", gap: 5 }}><span>ℹ️</span> {sub}</div>}
      <div style={{ position: "absolute", right: -25, bottom: -25, width: 90, height: 90, borderRadius: "50%", background: color + "08", pointerEvents: "none" }} />
    </div>
  );
}

function ProgBar({ pct, color = "var(--brand)" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: "var(--border-light)", borderRadius: 99 }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", borderRadius: 99, background: color }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 800, color, minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

function Modal({ onClose, children, width = 740 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ width, maxWidth: "96vw" }}>
        {children}
      </div>
    </div>
  );
}

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
      {tabs.map(t => <button key={t.id} className={`tab ${active === t.id ? "on" : ""}`} onClick={() => onChange(t.id)}>{t.label}</button>)}
    </div>
  );
}

function Search({ value, onChange, placeholder = "Search…" }) {
  return (
    <div className="sw" style={{ flex: 1, maxWidth: 380 }}>
      <input className="si" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function PlanPill({ type, tier, active }) {
  if (!tier) return <span style={{ color: "var(--text-3)", fontSize: 12 }}>—</span>;
  const c = planColor(type);
  return (
    <span style={{ background: active ? c + "22" : "#f0f2f6", color: active ? c : "#8f96a3", border: `1.5px solid ${active ? c + "55" : "#e8eaef"}`, padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 5 }}>
      {planIcon(type)} {tierLabel(tier)}{!active && <span style={{ opacity: .7 }}> (paused)</span>}
    </span>
  );
}

function InfoGrid({ items }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {items.map(([l, v, ic]) => (
        <div key={l} style={{ padding: "12px 16px", background: "var(--surface-alt)", borderRadius: 10, border: "1.5px solid var(--border-light)" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 4 }}>{ic} {l}</div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-1)", wordBreak: "break-word" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function PageButtons({ page, pages, setPage }) {
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 18 }}>
      <button className="btn bo" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
      {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
        <button key={p} className={`btn ${p === page ? "bp" : "bo"}`} onClick={() => setPage(p)}>{p}</button>
      ))}
      <button className="btn bo" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
    </div>
  );
}

function Sparkline({ data = [], color = "#1d240e", height = 64 }) {
  const values = data.map((item) => Number(item.value || item.page_views || item.total || 0));
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const width = 180;
  const innerH = height - 10;
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((value, index) => {
    const x = index * step;
    const pct = max === min ? 0.5 : (value - min) / (max - min);
    const y = height - 5 - (pct * innerH);
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
      <polyline fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}

function ChartCard({ title, subtitle, children, accent = "#1d240e" }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>{subtitle}</div>}
        </div>
        <div style={{ width: 14, height: 14, borderRadius: 99, background: accent }} />
      </div>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ASSIGN PLAN MODAL
══════════════════════════════════════════════════════════ */
function AssignPlanModal({ recruiter, onClose, onAssigned, flash }) {
  const [planType, setPlanType] = useState("job");
  const [tier, setTier] = useState("value");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const cfg = PLAN_META[planType]?.tiers[tier];
  const price = cfg?.price || 0;
  const gst = Math.round(price * 0.18);

  const handleAssign = async () => {
    setLoading(true); setErr("");
    try {
      await axios.post(`${API}/assign-plan`, { employer_id: recruiter.id, plan_type: planType, tier }, { headers: authHdr() });
      onAssigned();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || "Failed to assign plan");
    } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} width={540}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22, paddingBottom: 18, borderBottom: "1.5px solid var(--border-light)" }}>
        <Av name={recruiter.name} size={48} letter={recruiter.avatar} />
        <div>
          <h2 style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>Assign Plan</h2>
          <p style={{ fontSize: 13, color: "var(--text-2)" }}>{recruiter.name} · {recruiter.company}</p>
        </div>
        <button onClick={onClose} className="btn bg" style={{ marginLeft: "auto", fontSize: 18, padding: "4px 8px" }}>✕</button>
      </div>

      {/* Plan Type */}
      <div style={{ marginBottom: 18 }}>
        <div className="fl" style={{ marginBottom: 10 }}>Plan Type</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {Object.entries(PLAN_META).map(([key, meta]) => (
            <div key={key} onClick={() => setPlanType(key)}
              style={{ padding: "12px 14px", borderRadius: 10, border: `2px solid ${planType === key ? planColor(key) : "var(--border)"}`, background: planType === key ? planColor(key) + "11" : "var(--surface-alt)", cursor: "pointer", transition: "all .15s" }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{planIcon(key)}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: planType === key ? planColor(key) : "var(--text-1)" }}>{meta.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tier */}
      <div style={{ marginBottom: 20 }}>
        <div className="fl" style={{ marginBottom: 10 }}>Tier</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {["value", "maxx", "supermaxx"].map(t => {
            const c = PLAN_META[planType].tiers[t];
            return (
              <div key={t} onClick={() => setTier(t)}
                style={{ padding: "12px 14px", borderRadius: 10, border: `2px solid ${tier === t ? "var(--brand)" : "var(--border)"}`, background: tier === t ? "var(--brand-light)" : "var(--surface-alt)", cursor: "pointer", transition: "all .15s", textAlign: "center" }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: tier === t ? "var(--brand)" : "var(--text-1)", marginBottom: 4 }}>{tierLabel(t)}</div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>₹{fmt(c.price)}</div>
                {c.jobLimit && <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.jobLimit} jobs</div>}
                {c.cvLimit && <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.cvLimit} CVs</div>}
                {c.interviewLimit && <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.interviewLimit} interviews</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div style={{ background: "var(--surface-alt)", borderRadius: 12, padding: 16, marginBottom: 18, border: "1.5px solid var(--border-light)" }}>
        {[["Base price", fmtMoney(price)], ["GST (18%)", fmtMoney(gst)]].map(([l, v]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: "var(--text-2)" }}>{l}</span>
            <span style={{ fontWeight: 700 }}>{v}</span>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1.5px solid var(--border-light)" }}>
          <span style={{ fontWeight: 800 }}>Total</span>
          <span style={{ fontWeight: 800, fontSize: 17, color: "var(--brand)" }}>{fmtMoney(price + gst)}</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-3)" }}>Valid for 1 year · Invoice auto-generated</div>
      </div>

      {err && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 12, fontWeight: 600 }}>⚠ {err}</div>}

      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn bo" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
        <button className="btn bp" onClick={handleAssign} disabled={loading} style={{ flex: 2, justifyContent: "center" }}>
          {loading ? "Assigning…" : `Assign ${tierLabel(tier)} ${PLAN_META[planType].label} →`}
        </button>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════
   RECRUITER DETAIL MODAL
══════════════════════════════════════════════════════════ */
function RecruiterModal({ recruiter, onClose, onStatusChange, onSubToggle, onAssignPlan }) {
  const [tab, setTab] = useState("overview");
  if (!recruiter) return null;
  const subs = recruiter.subscriptions || {};

  return (
    <Modal onClose={onClose} width={820}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 22, paddingBottom: 18, borderBottom: "1.5px solid var(--border-light)" }}>
        <Av name={recruiter.name} size={64} letter={recruiter.avatar} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>{recruiter.name}</h2>
            <StatusChip status={recruiter.status} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-2)", marginBottom: 5 }}>{recruiter.company}</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {recruiter.email && <span style={{ fontSize: 12, color: "var(--text-3)" }}>✉️ {recruiter.email}</span>}
            {recruiter.phone && <span style={{ fontSize: 12, color: "var(--text-3)" }}>📱 {recruiter.phone}</span>}
            {recruiter.city && <span style={{ fontSize: 12, color: "var(--text-3)" }}>📍 {recruiter.city}</span>}
            {recruiter.industry && <span style={{ fontSize: 12, color: "var(--text-3)" }}>🏭 {recruiter.industry}</span>}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: "var(--green)" }}>{fmtMoney(recruiter.total_revenue || 0)}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>Total Revenue</div>
        </div>
        <button onClick={onClose} className="btn bg" style={{ fontSize: 18, padding: "4px 8px" }}>✕</button>
      </div>

      <Tabs
        tabs={[{ id: "overview", label: "📋 Overview" }, { id: "subscriptions", label: "💳 Subscriptions" }, { id: "actions", label: "⚙️ Actions" }]}
        active={tab} onChange={setTab}
      />

      {tab === "overview" && (
        <InfoGrid items={[
          ["Company", recruiter.company || "—", "🏢"],
          ["Email", recruiter.email || "—", "✉️"],
          ["Phone", recruiter.phone || "—", "📱"],
          ["City", recruiter.city || "—", "📍"],
          ["Industry", recruiter.industry || "—", "🏭"],
          ["GST", recruiter.gst || "—", "🏛️"],
          ["Status", recruiter.status || "—", "🟢"],
          ["Joined", fmtDate(recruiter.joinDate), "📅"],
          ["Total Revenue", fmtMoney(recruiter.total_revenue || 0), "💰"],
          ["Notes", recruiter.notes || "—", "📝"],
        ]} />
      )}

      {tab === "subscriptions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {["job", "cv", "salary", "career"].map(type => {
            const s = subs[type];
            const c = planColor(type);
            return (
              <div key={type} style={{ padding: "16px 20px", borderRadius: 12, border: `2px solid ${s?.active ? c + "55" : "var(--border)"}`, background: s?.active ? c + "08" : "var(--surface-alt)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{ fontSize: 28 }}>{planIcon(type)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                    <span style={{ fontWeight: 800, fontSize: 14, color: c }}>{PLAN_META[type].label}</span>
                    {s ? <PlanPill type={type} tier={s.plan} active={s.active} /> : <span style={{ fontSize: 12, color: "var(--text-3)" }}>Not subscribed</span>}
                  </div>
                  {s && (
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                      {s.jobLimit > 0 && <span style={{ fontSize: 12, color: "var(--text-3)" }}>Jobs: {s.jobsPosted}/{s.jobLimit}</span>}
                      {s.cvLimit > 0 && <span style={{ fontSize: 12, color: "var(--text-3)" }}>CVs: {s.cvsUsed}/{s.cvLimit}</span>}
                      {s.interviewLimit > 0 && <span style={{ fontSize: 12, color: "var(--text-3)" }}>Interviews: {s.interviewsUsed}/{s.interviewLimit}</span>}
                      {s.expiresAt && <span style={{ fontSize: 12, color: "var(--text-3)" }}>Expires: {fmtDate(s.expiresAt)}</span>}
                    </div>
                  )}
                </div>
                {s && (
                  <button className={s.active ? "btn bd" : "btn bs"} style={{ padding: "7px 14px", fontSize: 12 }}
                    onClick={() => onSubToggle(recruiter.id, type, !s.active)}>
                    {s.active ? "⏸ Pause" : "▶ Resume"}
                  </button>
                )}
              </div>
            );
          })}
          <button className="btn bp" style={{ marginTop: 6 }} onClick={() => { onClose(); onAssignPlan(recruiter); }}>
            ➕ Assign / Upgrade Plan
          </button>
        </div>
      )}

      {tab === "actions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ padding: "16px 20px", background: "var(--surface-alt)", borderRadius: 12, border: "1.5px solid var(--border-light)" }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Change Account Status</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[["active", "✅ Set Active", "bs"], ["pending", "⏳ Set Pending", "bo"], ["blocked", "🔒 Block", "bd"]].map(([s, l, cls]) => (
                <button key={s} className={`btn ${recruiter.status === s ? "bp" : cls}`}
                  style={{ padding: "8px 18px" }}
                  onClick={() => { onStatusChange(recruiter.id, s); onClose(); }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {recruiter.email && (
            <a href={`mailto:${recruiter.email}`} className="btn bo" style={{ justifyContent: "center", padding: 12 }}>
              ✉️ Email {recruiter.name}
            </a>
          )}
          <a href={`/company/${recruiter.id}`} target="_blank" rel="noreferrer" className="btn bo" style={{ justifyContent: "center", textDecoration: "none" }}>
            🏢 View Public Company Page
          </a>
          <div style={{ padding: "14px 16px", background: "var(--yellow-light)", borderRadius: 10, fontSize: 13, color: "var(--yellow)" }}>
            ⚠️ Blocking prevents the recruiter from posting jobs or accessing the platform.
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════
   CANDIDATE DETAIL MODAL
══════════════════════════════════════════════════════════ */
function CandidateModal({ candidate, onClose, onStatusChange }) {
  if (!candidate) return null;
  const skills = parseListValue(candidate.skills);
  const portfolio = candidate.portfolio || candidate.website || candidate.portfolio_url || "";
  const profileLink = candidate.username ? `/candidate/${candidate.username}` : null;
  const videoResume = candidate.video_resume_url || candidate.video_resume || "";
  const status = String(candidate.status || "active").toLowerCase();
  return (
    <Modal onClose={onClose} width={600}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 22, paddingBottom: 18, borderBottom: "1.5px solid var(--border-light)" }}>
        <Av name={candidate.name} size={60} letter={candidate.avatar} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h2 style={{ fontWeight: 800, fontSize: 19 }}>{candidate.name}</h2>
            {candidate.verified && <Chip label="✓ Verified" bg="#dcfce7" color="#16a34a" />}
            {candidate.resume && <Chip label="📄 Resume" bg="#dbeafe" color="#2563eb" />}
            <StatusChip status={status} />
          </div>
          <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 6 }}>{candidate.email}</p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {candidate.phone && <span style={{ fontSize: 12, color: "var(--text-3)" }}>📱 {candidate.phone}</span>}
            {candidate.location && <span style={{ fontSize: 12, color: "var(--text-3)" }}>📍 {candidate.location}</span>}
            {candidate.experience && <span style={{ fontSize: 12, color: "var(--text-3)" }}>💼 {candidate.experience}</span>}
          </div>
        </div>
        <button onClick={onClose} className="btn bg" style={{ fontSize: 18, padding: "4px 8px" }}>✕</button>
      </div>

      <InfoGrid items={[
        ["Applications", candidate.appliedJobs || 0, "🚀"],
        ["Profile Score", `${candidate.profileScore || 0}%`, "📊"],
        ["Resume", candidate.resume ? "Uploaded" : "Not uploaded", "📄"],
        ["Joined", fmtDate(candidate.joinDate), "📅"],
      ]} />

      <div style={{ margin: "16px 0" }}>
        <div className="fl">Profile Completion</div>
        <ProgBar pct={candidate.profileScore || 0} />
      </div>

      <InfoGrid items={[
        ["Portfolio", portfolio ? "Available" : "Not linked", "🔗"],
        ["Video Resume", videoResume ? "Available" : "Not linked", "🎥"],
        ["Account Status", status, "🛡️"],
        ["Applications", candidate.appliedJobs || 0, "🚀"],
      ]} />

      {(profileLink || videoResume || candidate.resume_url) && (
        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          {profileLink && (
            <a href={profileLink} target="_blank" rel="noreferrer" className="btn bo" style={{ justifyContent: "center", textDecoration: "none" }}>
              🔗 View Public Profile
            </a>
          )}
          {videoResume && (
            <a href={videoResume} target="_blank" rel="noreferrer" className="btn bo" style={{ justifyContent: "center", textDecoration: "none" }}>
              🎥 Watch Video Resume
            </a>
          )}
          {candidate.resume_url && (
            <a href={candidate.resume_url} target="_blank" rel="noreferrer" className="btn bp" style={{ justifyContent: "center", textDecoration: "none" }}>
              📄 View Resume
            </a>
          )}
        </div>
      )}

      <div style={{ marginTop: 16, padding: "16px 18px", background: "var(--surface-alt)", borderRadius: 12, border: "1.5px solid var(--border-light)" }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Account Control</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn bs" onClick={() => onStatusChange?.(candidate.id, "active")}>Unblock</button>
          <button className="btn bd" onClick={() => onStatusChange?.(candidate.id, "blocked")}>Block</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-3)", lineHeight: 1.6 }}>
          Blocking hides the candidate from recruiter-facing views once the recruiter data is refreshed.
        </div>
      </div>

      {skills.length > 0 && (
        <div>
          <div className="fl">Skills</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {skills.map(s => (
              <span key={s} style={{ background: "var(--brand-light)", color: "var(--brand)", padding: "5px 12px", borderRadius: 99, fontSize: 12.5, fontWeight: 700, border: "1.5px solid #ffd4a8" }}>{s}</span>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════
   JOB DETAIL MODAL
══════════════════════════════════════════════════════════ */
function JobModal({ job, onClose, onUpdateJob, onDeleteJob }) {
  const [busy, setBusy] = useState(false);

  const patch = async fields => {
    setBusy(true);
    try {
      await axios.patch(`${API}/jobs/${job.id}`, fields, { headers: authHdr() });
      onUpdateJob(job.id, fields);
    } finally { setBusy(false); }
  };

  if (!job) return null;
  const skills = (job.skills || "").split(",").map(s => s.trim()).filter(Boolean);

  return (
    <Modal onClose={onClose} width={700}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 22, paddingBottom: 18, borderBottom: "1.5px solid var(--border-light)" }}>
        <div style={{ width: 58, height: 58, borderRadius: 14, background: "var(--brand-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>🏢</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h2 style={{ fontWeight: 800, fontSize: 19 }}>{job.title}</h2>
            <StatusChip status={job.status} />
            {job.urgent && <Chip label="🔥 Urgent" bg="#fee2e2" color="#263012" />}
            {job.featured && <Chip label="⭐ Featured" bg="#fef3c7" color="#263012" />}
          </div>
          <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 6 }}>{job.company_name || job.company}</p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {job.location && <span style={{ fontSize: 12, color: "var(--text-3)" }}>📍 {job.location}</span>}
            {job.type && <span style={{ fontSize: 12, color: "var(--text-3)" }}>💼 {job.type}</span>}
            {job.category && <span style={{ fontSize: 12, color: "var(--text-3)" }}>🏷️ {job.category}</span>}
            {job.experience && <span style={{ fontSize: 12, color: "var(--text-3)" }}>📅 {job.experience}</span>}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--blue)" }}>{job.applications || 0}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>Applicants</div>
        </div>
        <button onClick={onClose} className="btn bg" style={{ fontSize: 18, padding: "4px 8px" }}>✕</button>
      </div>

      <InfoGrid items={[
        ["Salary", job.salary || "—", "💰"],
        ["Experience", job.experience || "—", "📅"],
        ["Posted", fmtDate(job.posted_date || job.created_at), "📅"],
        ["Expires", fmtDate(job.expires_date), "⏳"],
        ["Applications", job.applications || 0, "🚀"],
        ["Status", job.status || "—", "🟢"],
      ]} />

      {skills.length > 0 && (
        <div style={{ margin: "16px 0" }}>
          <div className="fl">Required Skills</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {skills.map(s => <span key={s} style={{ background: "var(--blue-light)", color: "var(--blue)", padding: "4px 11px", borderRadius: 99, fontSize: 12, fontWeight: 700 }}>{s}</span>)}
          </div>
        </div>
      )}

      {job.description && (
        <div style={{ margin: "16px 0", padding: "14px 16px", background: "var(--surface-alt)", borderRadius: 10, border: "1.5px solid var(--border-light)" }}>
          <div className="fl">Description</div>
          <p style={{ fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.75 }}>{job.description.slice(0, 600)}{job.description.length > 600 ? "…" : ""}</p>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, paddingTop: 16, borderTop: "1.5px solid var(--border-light)", flexWrap: "wrap" }}>
        <button className="btn bo" disabled={busy} onClick={() => patch({ status: job.status === "active" ? "closed" : "active" })}>
          {job.status === "active" ? "🔒 Close Job" : "✅ Reopen Job"}
        </button>
        <button className="btn bo" disabled={busy} onClick={() => patch({ urgent: job.urgent ? 0 : 1 })}>
          {job.urgent ? "🔥 Remove Urgent" : "🔥 Mark Urgent"}
        </button>
        <button className="btn bo" disabled={busy} onClick={() => patch({ featured: job.featured ? 0 : 1 })}>
          {job.featured ? "⭐ Unfeature" : "⭐ Feature"}
        </button>
        {onDeleteJob && (
          <button className="btn bo" disabled={busy} onClick={() => onDeleteJob(job.id)}>
            Delete Job
          </button>
        )}
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════
   INVOICE MODAL
══════════════════════════════════════════════════════════ */
function InvoiceModal({ txn, onClose }) {
  if (!txn) return null;
  return (
    <Modal onClose={onClose} width={520}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>🏛️</div>
        <h2 style={{ fontWeight: 800, fontSize: 20 }}>Tax Invoice</h2>
        <p style={{ color: "var(--text-3)", fontSize: 13, marginTop: 4 }}>{txn.invoiceId}</p>
      </div>
      <div style={{ border: "2px solid var(--border)", borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ background: "var(--surface-alt)", padding: "14px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>BILLED TO</div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{txn.company}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{txn.mode}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>DATE</div>
            <div style={{ fontWeight: 700 }}>{fmtDate(txn.date)}</div>
          </div>
        </div>
        <div style={{ padding: "16px 20px" }}>
          {[
            [`${txn.planName} Plan (${txn.planType})`, fmtMoney(txn.amount)],
            ["GST (18%)", fmtMoney(txn.gst)],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: "var(--text-2)" }}>{l}</span>
              <span style={{ fontWeight: 700 }}>{v}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "2px solid var(--border)", marginTop: 8 }}>
            <span style={{ fontWeight: 800, fontSize: 16 }}>Total</span>
            <span style={{ fontWeight: 800, fontSize: 18, color: "var(--green)" }}>{fmtMoney(txn.total)}</span>
          </div>
        </div>
        <div style={{ padding: "10px 20px", background: txn.status === "paid" ? "#dcfce7" : "#fee2e2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: txn.status === "paid" ? "#16a34a" : "#263012" }}>{txn.status === "paid" ? "✅ PAID" : "❌ UNPAID"}</span>
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>via {txn.mode}</span>
        </div>
      </div>
      <button onClick={onClose} className="btn bp" style={{ width: "100%", justifyContent: "center" }}>Close</button>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════
   POST JOB MODAL
══════════════════════════════════════════════════════════ */
function PostJobModal({ recruiters, onClose, onPosted, flash }) {
  const [form, setForm] = useState({ title: "", company: "", employer_id: "", location: "", type: "Full-time", category: "Technology", salary: "", experience: "", skills: "", description: "", urgent: false, featured: false });
  const [loading, setLoading] = useState(false);
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title || !form.company || !form.location) { flash("Title, company and location are required", "error"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/jobs`, form, { headers: authHdr() });
      onPosted(); onClose(); flash("Job posted successfully!");
    } catch (e) { flash(e.response?.data?.error || "Failed to post job", "error"); }
    finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} width={680}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22, paddingBottom: 18, borderBottom: "1.5px solid var(--border-light)" }}>
        <h2 style={{ fontWeight: 800, fontSize: 19 }}>➕ Post New Job</h2>
        <button onClick={onClose} className="btn bg" style={{ fontSize: 18, padding: "4px 8px" }}>✕</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: 14 }}>
        {[["Job Title", "title", "text", "e.g. Senior React Developer"], ["Company", "company", "text", "Company name"], ["Location", "location", "text", "e.g. Mumbai, Delhi"], ["Salary", "salary", "text", "e.g. ₹8-12 LPA"], ["Experience", "experience", "text", "e.g. 2-4 years"], ["Skills", "skills", "text", "Comma-separated skills"]].map(([l, k, t, ph]) => (
          <div key={k}>
            <label className="fl">{l}</label>
            <input className="fi" type={t} placeholder={ph} value={form[k]} onChange={e => F(k, e.target.value)} />
          </div>
        ))}
        <div>
          <label className="fl">Job Type</label>
          <select className="fi" value={form.type} onChange={e => F("type", e.target.value)}>
            {["Full-time", "Part-time", "Contract", "Internship", "Remote", "Hybrid"].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="fl">Category</label>
          <select className="fi" value={form.category} onChange={e => F("category", e.target.value)}>
            {["Technology", "Finance", "Marketing", "HR", "Operations", "Sales", "Healthcare", "Education", "Other"].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="fl">Linked Recruiter (optional)</label>
          <select className="fi" value={form.employer_id} onChange={e => F("employer_id", e.target.value)}>
            <option value="">— None —</option>
            {recruiters.map(r => <option key={r.id} value={r.id}>{r.name} · {r.company}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 20, paddingTop: 18 }}>
          {[["urgent", "🔥 Urgent"], ["featured", "⭐ Featured"]].map(([k, l]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
              <input type="checkbox" checked={form[k]} onChange={e => F(k, e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--brand)" }} />
              {l}
            </label>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label className="fl">Description</label>
        <textarea className="fi" rows={4} placeholder="Job description…" value={form.description} onChange={e => F("description", e.target.value)} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn bo" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
        <button className="btn bp" onClick={submit} disabled={loading} style={{ flex: 2, justifyContent: "center" }}>{loading ? "Posting…" : "Post Job →"}</button>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE: DASHBOARD
══════════════════════════════════════════════════════════ */
function DashboardPage({ stats, recruiters, jobs, transactions, complaints, complaintSummary, activity, activityCounts, analytics, onViewRecruiter, onViewJob }) {
  const topRecruiters = [...recruiters].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0)).slice(0, 6);
  const topJobs = [...jobs].sort((a, b) => (b.applications || 0) - (a.applications || 0)).slice(0, 6);
  const recentTxns = transactions.slice(0, 8);
  const trend = analytics?.daily || {};

  return (
    <div className="sf dashboard-container">
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 26, fontWeight: 900, marginBottom: 6, color: "var(--text-1)" }}>Admin Command Center</h2>
        <p style={{ color: "var(--text-3)", fontSize: 14.5 }}>Platform performance insights and operational metrics for IHN.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginBottom: 32 }}>
        <StatCard icon="💰" label="Total Revenue" value={fmtMoney(stats.total_revenue || 0)} color="#16a34a" trend={18} trendLabel="vs last month" />
        <StatCard icon="👥" label="Candidates" value={fmt(stats.total_candidates || 0)} color="#263012" trend={12} />
        <StatCard icon="🏢" label="Active Jobs" value={fmt(stats.active_jobs || 0)} sub={`${stats.pending_jobs || 0} pending`} color="#1d240e" trend={8} />
        <StatCard icon="🤝" label="Active Recruiters" value={fmt(stats.active_employers || 0)} sub={`${stats.pending_employers || 0} pending`} color="#7c3aed" />
        <StatCard icon="🚀" label="Total Applications" value={fmt(stats.total_applications || 0)} color="#0284c7" trend={23} />
        <StatCard icon="🧾" label="Complaints" value={fmt(complaintSummary?.total || complaints.length || 0)} sub={`${fmt(complaintSummary?.open || 0)} open`} color="#dc2626" />
        <StatCard icon="⭐" label="Reviews" value={fmt(stats.total_reviews || 0)} sub={`${fmt(stats.pending_reviews || 0)} pending`} color="#263012" />
        <StatCard icon="📁" label="Documents" value={fmt(stats.total_documents || 0)} sub="Recruiter uploads" color="#0f766e" />
        <StatCard icon="🔐" label="Access Grants" value={fmt(stats.active_access_grants || 0)} sub="Active shares" color="#2563eb" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginBottom: 32 }}>
        {[
          { key: "candidates", label: "New Candidates", color: "#263012", data: trend.candidates || [] },
          { key: "jobs", label: "New Jobs", color: "#2563eb", data: trend.jobs || [] },
          { key: "recruiters", label: "New Recruiters", color: "#16a34a", data: trend.recruiters || [] },
          { key: "page_usage", label: "Page Views", color: "#7c3aed", data: trend.page_usage || [] },
        ].map((item) => (
          <ChartCard key={item.key} title={item.label} subtitle="Last 14 days" accent={item.color}>
            <div style={{ fontSize: 28, fontWeight: 900, color: item.color, marginBottom: 8, letterSpacing: "-1px" }}>
              {fmt((item.data || []).slice(-7).reduce((sum, row) => sum + Number(row.value || row.page_views || 0), 0))}
            </div>
            <Sparkline data={item.data} color={item.color} height={54} />
          </ChartCard>
        ))}
      </div>

      <div className="dashboard-grid" style={{ gap: 24, marginBottom: 32 }}>
        <div className="card">
          <div style={{ padding: "20px 24px", borderBottom: "1.5px solid var(--border-light)", fontWeight: 800, fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>💰 Top Recruiters</span> <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>BY REVENUE</span></div>
          {topRecruiters.map((r, i) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 20px", borderBottom: "1px solid var(--border-light)", cursor: "pointer", transition: "background .15s" }}
              onClick={() => onViewRecruiter(r)} onMouseEnter={e => e.currentTarget.style.background = "#fafbff"} onMouseLeave={e => e.currentTarget.style.background = ""}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--brand)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 10, flexShrink: 0 }}>{i + 1}</div>
              <Av name={r.name} size={32} letter={r.avatar} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.name}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{r.company}</div>
              </div>
              <div style={{ fontWeight: 800, color: "var(--green)", fontSize: 14 }}>{fmtMoney(r.total_revenue || 0)}</div>
            </div>
          ))}
          {!topRecruiters.length && <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)" }}>No recruiters yet</div>}
        </div>

        <div className="card">
          <div style={{ padding: "20px 24px", borderBottom: "1.5px solid var(--border-light)", fontWeight: 800, fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>🔥 Popular Jobs</span> <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>BY APPLICATIONS</span></div>
          {topJobs.map((j, i) => (
            <div key={j.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 20px", borderBottom: "1px solid var(--border-light)", cursor: "pointer", transition: "background .15s" }}
              onClick={() => onViewJob(j)} onMouseEnter={e => e.currentTarget.style.background = "#fafbff"} onMouseLeave={e => e.currentTarget.style.background = ""}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--blue)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 10, flexShrink: 0 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{j.title}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{j.company_name || j.company} · {j.location}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 800, fontSize: 18, color: "var(--brand)" }}>{j.applications || 0}</div>
                <div style={{ fontSize: 10, color: "var(--text-3)" }}>applied</div>
              </div>
            </div>
          ))}
          {!topJobs.length && <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)" }}>No jobs yet</div>}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 32 }}>
        <div style={{ padding: "20px 24px", borderBottom: "1.5px solid var(--border-light)", fontWeight: 800, fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>🧭 Platform Activity</span> <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>RECENT TIMELINE</span></div>
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          {activity.slice(0, 10).map((item) => (
            <div key={item.id} style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "14px 24px", borderBottom: "1px solid var(--border-light)" }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: item.section === "complaints" ? "#fee2e2" : item.section === "reviews" ? "#fef3c7" : item.section === "jobs" ? "#dbeafe" : item.section === "transactions" ? "#dcfce7" : "#f0f2f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {item.section === "complaints" ? "🧾" : item.section === "reviews" ? "⭐" : item.section === "jobs" ? "🏢" : item.section === "transactions" ? "💳" : item.section === "access" ? "🔐" : "📎"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{item.title}</div>
                <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>{item.message}</div>
              </div>
              <div style={{ textAlign: "right", fontSize: 11, color: "var(--text-3)", flexShrink: 0, fontWeight: 600 }}>
                <div>{fmtDate(item.created_at)}</div>
                <div style={{ textTransform: "capitalize" }}>{item.section}</div>
              </div>
            </div>
          ))}
        </div>
        {!activity.length && <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)" }}>No activity yet</div>}
      </div>

      <div className="card" style={{ marginBottom: 32 }}>
        <div style={{ padding: "20px 24px", borderBottom: "1.5px solid var(--border-light)", fontWeight: 800, fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>💳 Financial Overview</span> <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>RECENT PAYMENTS</span></div>
        <div className="table-responsive">
          <div className="th" style={{ gridTemplateColumns: "minmax(180px, 2fr) minmax(140px, 1.5fr) 1fr 1fr 1fr 1fr" }}>
            <span>Company</span><span>Plan</span><span>Amount</span><span>GST</span><span>Total</span><span>Status</span>
          </div>
          {recentTxns.map((t, i) => (
            <div key={i} className="tr" style={{ gridTemplateColumns: "minmax(180px, 2fr) minmax(140px, 1.5fr) 1fr 1fr 1fr 1fr" }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t.company}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span>{planIcon(t.planType)}</span>
                <span style={{ fontSize: 13, color: "var(--text-2)" }}>{t.planName}</span>
              </div>
              <span style={{ fontSize: 13 }}>{fmtMoney(t.amount)}</span>
              <span style={{ fontSize: 13 }}>{fmtMoney(t.gst)}</span>
              <span style={{ fontWeight: 800, color: "var(--green)" }}>{fmtMoney(t.total)}</span>
              <StatusChip status={t.status} />
            </div>
          ))}
        </div>
        {!recentTxns.length && <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)" }}>No transactions</div>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE: RECRUITERS
══════════════════════════════════════════════════════════ */
function RecruitersPage({ recruiters, onView, onAssignPlan }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PER = 15;

  const list = recruiters.filter(r => {
    const q = search.toLowerCase();
    const m = !q || (r.name || "").toLowerCase().includes(q) || (r.company || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q) || (r.city || "").toLowerCase().includes(q);
    const f = filter === "all" || r.status === filter;
    return m && f;
  }).sort((a, b) => new Date(b.joinDate) - new Date(a.joinDate));

  const pages = Math.ceil(list.length / PER);
  const paged = list.slice((page - 1) * PER, page * PER);

  return (
    <div className="sf">
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search by name, company, email, city…" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all", "All"], ["active", "Active"], ["pending", "Pending"], ["blocked", "Blocked"]].map(([f, l]) => (
            <button key={f} className={`tab ${filter === f ? "on" : ""}`} onClick={() => { setFilter(f); setPage(1); }}>
              {l} <span style={{ fontSize: 10, opacity: .8 }}>({recruiters.filter(r => f === "all" || r.status === f).length})</span>
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} recruiters</span>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "2.5fr 1.5fr 1fr 2.5fr 1fr 110px" }}>
          <span>Recruiter</span><span>Company</span><span>Status</span><span>Active Plans</span><span>Revenue</span><span></span>
        </div>
        {paged.map((r, i) => {
          const activePlans = Object.entries(r.subscriptions || {}).filter(([, s]) => s?.active);
          return (
            <div key={r.id || i} className="tr" style={{ gridTemplateColumns: "2.5fr 1.5fr 1fr 2.5fr 1fr 110px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Av name={r.name} size={34} letter={r.avatar} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>{r.email}</div>
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.company}</div>
                {r.city && <div style={{ fontSize: 11, color: "var(--text-3)" }}>📍 {r.city}</div>}
              </div>
              <StatusChip status={r.status} />
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {activePlans.length
                  ? activePlans.map(([type, s]) => <PlanPill key={type} type={type} tier={s.plan} active={s.active} />)
                  : <span style={{ fontSize: 12, color: "var(--text-3)" }}>No active plans</span>}
              </div>
              <span style={{ fontWeight: 800, color: "var(--green)", fontSize: 13.5 }}>{fmtMoney(r.total_revenue || 0)}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn bp" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => onView(r)}>View</button>
                <button className="btn bo" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onAssignPlan(r)} title="Assign Plan">➕</button>
              </div>
            </div>
          );
        })}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No recruiters found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE: JOBS
══════════════════════════════════════════════════════════ */
function JobsPage({ jobs, recruiters, onView, onUpdateJob, onPosted, flash }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showPost, setShowPost] = useState(false);
  const [page, setPage] = useState(1);
  const PER = 15;

  const list = jobs.filter(j => {
    const q = search.toLowerCase();
    const m = !q || (j.title || "").toLowerCase().includes(q) || (j.company_name || j.company || "").toLowerCase().includes(q) || (j.location || "").toLowerCase().includes(q);
    const f = filter === "all" || j.status === filter || (filter === "urgent" && j.urgent) || (filter === "featured" && j.featured);
    return m && f;
  }).sort((a, b) => new Date(b.created_at || b.posted_date) - new Date(a.created_at || a.posted_date));

  const pages = Math.ceil(list.length / PER);
  const paged = list.slice((page - 1) * PER, page * PER);

  return (
    <div className="sf">
      {showPost && <PostJobModal recruiters={recruiters} onClose={() => setShowPost(false)} onPosted={onPosted} flash={flash} />}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search by title, company, location…" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all", "All"], ["active", "Active"], ["closed", "Closed"], ["draft", "Draft"], ["urgent", "🔥 Urgent"], ["featured", "⭐ Featured"]].map(([f, l]) => (
            <button key={f} className={`tab ${filter === f ? "on" : ""}`} onClick={() => { setFilter(f); setPage(1); }}>{l}</button>
          ))}
        </div>
        <button className="btn bp" style={{ marginLeft: "auto" }} onClick={() => setShowPost(true)}>➕ Post Job</button>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "2.5fr 1.5fr 1fr 1fr 1fr 1fr 90px" }}>
          <span>Job Title</span><span>Company</span><span>Type</span><span>Applicants</span><span>Posted</span><span>Status</span><span></span>
        </div>
        {paged.map((j, i) => (
          <div key={j.id || i} className="tr" style={{ gridTemplateColumns: "2.5fr 1.5fr 1fr 1fr 1fr 1fr 90px" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 13.5 }}>{j.title}</span>
                {j.urgent && <Chip label="🔥" bg="#fee2e2" color="#263012" style={{ padding: "1px 7px", fontSize: 10 }} />}
                {j.featured && <Chip label="⭐" bg="#fef3c7" color="#263012" style={{ padding: "1px 7px", fontSize: 10 }} />}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{j.location}</div>
            </div>
            <span style={{ fontSize: 13, color: "var(--text-2)" }}>{j.company_name || j.company || "—"}</span>
            <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>{j.type || "—"}</span>
            <span style={{ fontWeight: 800, fontSize: 16, color: j.applications > 0 ? "var(--brand)" : "var(--text-3)" }}>{j.applications || 0}</span>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(j.posted_date || j.created_at)}</span>
            <StatusChip status={j.status} />
            <button className="btn bo" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => onView(j)}>View</button>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No jobs found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE: CANDIDATES
══════════════════════════════════════════════════════════ */
function CandidatesPage({ candidates, onView, onStatusChange }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PER = 15;

  const list = candidates.filter(c => {
    const q = search.toLowerCase();
    const m = !q || (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.username || "").toLowerCase().includes(q) || safeStr(c.skills).toLowerCase().includes(q) || (c.location || "").toLowerCase().includes(q);
    const f = filter === "all"
      || (filter === "resume" && c.resume)
      || (filter === "verified" && c.verified)
      || (filter === "fresher" && c.experience === "Fresher")
      || (filter === "experienced" && c.experience === "Experienced")
      || (filter === "applied" && c.appliedJobs > 0)
      || (filter === "active" && String(c.status || "").toLowerCase() === "active")
      || (filter === "blocked" && String(c.status || "").toLowerCase() === "blocked");
    return m && f;
  }).sort((a, b) => new Date(b.joinDate) - new Date(a.joinDate));

  const pages = Math.ceil(list.length / PER);
  const paged = list.slice((page - 1) * PER, page * PER);

  return (
    <div className="sf">
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search by name, email, skills, location…" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all", "All"], ["active", "Active"], ["blocked", "Blocked"], ["resume", "Has Resume"], ["verified", "Verified"], ["fresher", "Freshers"], ["experienced", "Experienced"], ["applied", "Applied"]].map(([f, l]) => (
            <button key={f} className={`tab ${filter === f ? "on" : ""}`} onClick={() => { setFilter(f); setPage(1); }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} candidates</span>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "2.2fr 1.7fr 1fr 1fr 1fr 1fr 1.1fr 130px" }}>
          <span>Candidate</span><span>Skills</span><span>Experience</span><span>Status</span><span>Applications</span><span>Completion</span><span>Joined</span><span></span>
        </div>
        {paged.map((c, i) => (
          <div key={c.id || i} className="tr" style={{ gridTemplateColumns: "2.2fr 1.7fr 1fr 1fr 1fr 1fr 1.1fr 130px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Av name={c.name} size={34} letter={c.avatar} />
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5 }}>{c.name}</span>
                  {c.verified && <Chip label="✓" bg="#dcfce7" color="#16a34a" style={{ padding: "1px 6px", fontSize: 10 }} />}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.email}</div>
              </div>
            </div>
            <span style={{ fontSize: 12, color: "var(--text-2)" }}>{safeStr(c.skills).slice(0, 42)}{safeStr(c.skills).length > 42 ? "…" : ""}</span>
            <StatusChip status={(c.experience || "").toLowerCase()} />
            <StatusChip status={c.status || "active"} />
            <span style={{ fontWeight: 800, fontSize: 16, color: c.appliedJobs > 0 ? "var(--brand)" : "var(--text-3)" }}>{c.appliedJobs || 0}</span>
            <ProgBar pct={c.profileScore || 0} />
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(c.joinDate)}</span>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="btn bp" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onView(c)}>View</button>
              {onStatusChange && (
                <button
                  className={String(c.status || "").toLowerCase() === "blocked" ? "btn bs" : "btn bd"}
                  style={{ padding: "6px 10px", fontSize: 12 }}
                  onClick={() => onStatusChange(c.id, String(c.status || "").toLowerCase() === "blocked" ? "active" : "blocked")}
                >
                  {String(c.status || "").toLowerCase() === "blocked" ? "Unblock" : "Block"}
                </button>
              )}
            </div>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No candidates found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE: TRANSACTIONS
══════════════════════════════════════════════════════════ */
function TransactionsPage({ transactions, onViewInvoice }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PER = 20;

  const list = transactions.filter(t => {
    const q = search.toLowerCase();
    const m = !q || (t.company || "").toLowerCase().includes(q) || (t.planName || "").toLowerCase().includes(q) || (t.invoiceId || "").toLowerCase().includes(q) || (t.planType || "").toLowerCase().includes(q);
    const f = filter === "all" || t.status === filter || t.planType === filter;
    return m && f;
  });

  const paidRevenue = list.filter(t => t.status === "paid").reduce((s, t) => s + (t.total || 0), 0);
  const pages = Math.ceil(list.length / PER);
  const paged = list.slice((page - 1) * PER, page * PER);

  return (
    <div className="sf">
      <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
        {[{ icon: "💰", label: "Filtered Revenue", value: fmtMoney(paidRevenue), color: "#16a34a" }, { icon: "📄", label: "Total Records", value: fmt(list.length), color: "#263012" }, { icon: "✅", label: "Paid", value: fmt(list.filter(t => t.status === "paid").length), color: "#16a34a" }].map(s => (
          <div key={s.label} className="sc" style={{ flex: 1 }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search by company, plan, invoice ID…" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all", "All"], ["paid", "Paid"], ["unpaid", "Unpaid"], ["job", "Job Plan"], ["cv", "CV Plan"], ["salary", "Salary Plan"], ["career", "Career Plan"]].map(([f, l]) => (
            <button key={f} className={`tab ${filter === f ? "on" : ""}`} onClick={() => { setFilter(f); setPage(1); }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} records</span>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 1fr 1fr 70px" }}>
          <span>Company</span><span>Plan</span><span>Amount</span><span>GST</span><span>Total</span><span>Date</span><span>Status</span><span></span>
        </div>
        {paged.map((t, i) => (
          <div key={i} className="tr" style={{ gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 1fr 1fr 70px" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t.company}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{t.invoiceId}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span>{planIcon(t.planType)}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.planName}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", textTransform: "capitalize" }}>{t.planType}</div>
              </div>
            </div>
            <span style={{ fontSize: 13 }}>{fmtMoney(t.amount)}</span>
            <span style={{ fontSize: 13 }}>{fmtMoney(t.gst)}</span>
            <span style={{ fontWeight: 800, color: "var(--green)" }}>{fmtMoney(t.total)}</span>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(t.date)}</span>
            <StatusChip status={t.status} />
            <button className="btn bo" style={{ padding: "5px 10px", fontSize: 12 }} onClick={() => onViewInvoice(t)}>🏛️</button>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No transactions found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE: ANALYTICS
══════════════════════════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
function AnalyticsPage({ stats, recruiters, jobs, transactions, candidates }) {
  const planRevenue = transactions.filter(t => t.status === "paid").reduce((acc, t) => { acc[t.planType] = (acc[t.planType] || 0) + (t.total || 0); return acc; }, {});
  const topSkills = Object.entries(
    candidates.reduce((acc, c) => { (c.skills || "").split(",").map(s => s.trim()).filter(Boolean).forEach(s => { acc[s] = (acc[s] || 0) + 1; }); return acc; }, {})
  ).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const topCities = Object.entries(
    candidates.reduce((acc, c) => { if (c.location) { acc[c.location] = (acc[c.location] || 0) + 1; } return acc; }, {})
  ).sort((a, b) => b[1] - a[1]).slice(0, 8);

  const freshers = candidates.filter(c => c.experience === "Fresher").length;
  const experienced = candidates.filter(c => c.experience === "Experienced").length;
  const withResume = candidates.filter(c => c.resume).length;
  const verified = candidates.filter(c => c.verified).length;
  const total = candidates.length || 1;

  const Bar = ({ data, max, color = "var(--brand)" }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map(([l, v]) => (
        <div key={l} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 130, fontSize: 12, color: "var(--text-2)", textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l}>{l}</div>
          <div style={{ flex: 1, height: 9, background: "var(--border-light)", borderRadius: 99 }}>
            <div style={{ width: `${(v / (max || 1)) * 100}%`, height: "100%", borderRadius: 99, background: color }} />
          </div>
          <div style={{ width: 36, fontSize: 12, fontWeight: 800, color, textAlign: "right" }}>{v}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="sf">
      <div className="stat-row">
        <StatCard icon="📄" label="Resume Rate" value={`${Math.round((withResume / total) * 100)}%`} sub={`${withResume} of ${total}`} color="#16a34a" />
        <StatCard icon="✓" label="Verified Candidates" value={`${Math.round((verified / total) * 100)}%`} sub={`${verified} verified`} color="#263012" />
        <StatCard icon="🛡️" label="Freshers" value={`${Math.round((freshers / total) * 100)}%`} sub={`${freshers} candidates`} color="#0284c7" />
        <StatCard icon="⚔️" label="Experienced" value={`${Math.round((experienced / total) * 100)}%`} sub={`${experienced} candidates`} color="#7c3aed" />
      </div>

      {/* Revenue by plan */}
      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 16 }}>💰 Revenue by Plan Type</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
          {["job", "cv", "salary", "career"].map(type => {
            const rev = planRevenue[type] || 0;
            const pct = Math.round((rev / ((stats.total_revenue || 0) || 1)) * 100);
            return (
              <div key={type} style={{ padding: "16px 18px", borderRadius: 12, border: `2px solid ${planColor(type)}33`, background: planColor(type) + "0d", textAlign: "center" }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>{planIcon(type)}</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: planColor(type) }}>{fmtMoney(rev)}</div>
                <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>{PLAN_META[type].label}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{pct}% of total</div>
                <div style={{ height: 4, background: planColor(type) + "33", borderRadius: 99, marginTop: 10 }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: planColor(type), borderRadius: 99 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        <div className="card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 16 }}>🔑 Top Skills in Demand</div>
          {topSkills.length ? <Bar data={topSkills} max={topSkills[0]?.[1] || 1} color="var(--brand)" /> : <p style={{ color: "var(--text-3)" }}>No data</p>}
        </div>
        <div className="card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 16 }}>📍 Top Candidate Cities</div>
          {topCities.length ? <Bar data={topCities} max={topCities[0]?.[1] || 1} color="var(--blue)" /> : <p style={{ color: "var(--text-3)" }}>No data</p>}
        </div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 16 }}>🏆 Top Jobs by Applications</div>
        {jobs.length ? (
          <Bar
            data={[...jobs].sort((a, b) => (b.applications || 0) - (a.applications || 0)).slice(0, 8).map(j => [(j.title || "—").slice(0, 26), j.applications || 0])}
            max={[...jobs].sort((a, b) => (b.applications || 0) - (a.applications || 0))[0]?.applications || 1}
            color="var(--purple)"
          />
        ) : <p style={{ color: "var(--text-3)" }}>No jobs data</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ROOT
══════════════════════════════════════════════════════════ */
function ReviewsPage({ reviews, summary, onView, onDelete, onSave }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState(null);
  const PER = 12;

  const q = search.toLowerCase();
  const list = reviews.filter((r) => {
    const match =
      !q ||
      (r.user_name || "").toLowerCase().includes(q) ||
      (r.user_email || "").toLowerCase().includes(q) ||
      (r.company_name || "").toLowerCase().includes(q) ||
      (r.comment || "").toLowerCase().includes(q) ||
      (r.tag || "").toLowerCase().includes(q);
    const status = String(r.status || "visible");
    return match && (filter === "all" || status === filter);
  });

  const pages = Math.max(1, Math.ceil(list.length / PER));
  const paged = list.slice((page - 1) * PER, page * PER);

  const openReview = (review) => {
    setDraft({
      ...review,
      comment: review.admin_modified_text ?? review.comment ?? review.review_text ?? "",
      rating: review.admin_modified_rating ?? review.rating ?? 0,
      admin_note: review.admin_note ?? "",
      status: review.status || "visible",
    });
  };

  const saveDraft = async () => {
    if (!draft) return;
    await onSave?.(draft);
    setDraft(null);
  };

  return (
    <div className="sf">
      <div className="stat-row">
        <StatCard icon="⭐" label="Total Reviews" value={summary?.total || reviews.length || 0} color="#263012" />
        <StatCard icon="⏳" label="Pending" value={summary?.pending || 0} color="#263012" />
        <StatCard icon="✅" label="Visible" value={summary?.visible || 0} color="#16a34a" />
        <StatCard icon="🛑" label="Removal requests" value={summary?.removal_requested || 0} color="#394420" />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search reviewer, company, comment..." />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all", "All"], ["visible", "Visible"], ["pending", "Pending"], ["removal_requested", "Removal requested"], ["hidden", "Hidden"]].map(([k, l]) => (
            <button key={k} className={`tab ${filter === k ? "on" : ""}`} onClick={() => { setFilter(k); setPage(1); }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} reviews</span>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "2fr 1.2fr 1fr 2.5fr 1fr 120px" }}>
          <span>Reviewer</span><span>Company</span><span>Rating</span><span>Review</span><span>Status</span><span></span>
        </div>
        {paged.map((r) => (
          <div key={r.id} className="tr" style={{ gridTemplateColumns: "2fr 1.2fr 1fr 2.5fr 1fr 120px", alignItems: "start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.user_name || "Anonymous"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{r.user_email || "—"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{[r.position, r.city].filter(Boolean).join(" · ")}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.company_name || "—"}</div>
              {r.tag && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{r.tag}</div>}
            </div>
            <div style={{ fontWeight: 800, color: "#263012", fontSize: 16 }}>{r.rating || 0}/5</div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.5 }}>
              {(r.comment || r.review || "—").slice(0, 150)}
              {(r.comment || r.review || "").length > 150 ? "…" : ""}
              {r.removal_requested_reason && <div style={{ marginTop: 6, fontSize: 11.5, color: "#b91c1c" }}>Removal request: {r.removal_requested_reason}</div>}
            </div>
            <StatusChip status={r.status} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button className="btn bp" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => openReview(r)}>Edit</button>
              <button className="btn bo" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onDelete?.(r.id)}>Delete</button>
            </div>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No reviews found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />

      {draft && (
        <Modal onClose={() => setDraft(null)} width={720}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, paddingBottom: 14, borderBottom: "1.5px solid var(--border-light)" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Edit Review</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>{draft.user_name} · {draft.company_name}</div>
            </div>
            <button className="btn bg" onClick={() => setDraft(null)}>✕</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label className="fl">Rating</label>
              <input className="fi" type="number" min="1" max="5" value={draft.rating} onChange={e => setDraft(d => ({ ...d, rating: Number(e.target.value || 0) }))} />
            </div>
            <div>
              <label className="fl">Status</label>
              <select className="fi" value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}>
                {["visible", "hidden", "pending", "removal_requested"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="fl">Review Text</label>
            <textarea className="fi" rows={4} value={draft.comment} onChange={e => setDraft(d => ({ ...d, comment: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="fl">Admin Note</label>
            <textarea className="fi" rows={3} value={draft.admin_note} onChange={e => setDraft(d => ({ ...d, admin_note: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="btn bo" onClick={() => setDraft(null)}>Cancel</button>
            <button className="btn bp" onClick={saveDraft}>Save Changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   COMPLAINT EDITOR
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function ComplaintEditorModal({ complaint, candidates, onClose, onSave, onCreate, flash }) {
  const isEdit = Boolean(complaint?.id);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(() => ({
    candidate_id: complaint?.candidate_id || (candidates[0]?.id || ""),
    title: complaint?.title || "",
    category: complaint?.category || "misconduct",
    message: complaint?.message || "",
    status: complaint?.status || "open",
    visible_on_profile: complaint?.visible_on_profile ?? true,
    hidden_reason: complaint?.hidden_reason || "",
    admin_note: complaint?.admin_note || "",
    terms_accepted: Boolean(isEdit),
  }));

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!isEdit && !form.candidate_id) {
      flash?.("Please choose a candidate", "error");
      return;
    }
    if (!form.title.trim() || !form.message.trim()) {
      flash?.("Title and message are required", "error");
      return;
    }
    if (!isEdit && !form.terms_accepted) {
      flash?.("Please accept the complaint terms", "error");
      return;
    }

    setBusy(true);
    try {
      if (isEdit) {
        await onSave?.({
          ...complaint,
          title: form.title.trim(),
          category: form.category,
          message: form.message.trim(),
          status: form.status,
          visible_on_profile: form.visible_on_profile,
          hidden_reason: form.hidden_reason,
          admin_note: form.admin_note,
        });
      } else {
        await onCreate?.({
          candidate_id: Number(form.candidate_id),
          title: form.title.trim(),
          category: form.category,
          message: form.message.trim(),
          visible_on_profile: form.visible_on_profile,
          terms_accepted: form.terms_accepted,
          proof: file,
        });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} width={760}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, paddingBottom: 14, borderBottom: "1.5px solid var(--border-light)" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{isEdit ? "Edit Complaint" : "Create Complaint"}</div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>{isEdit ? complaint?.candidate_name || "Complaint record" : "File a new complaint on behalf of the admin team"}</div>
        </div>
        <button className="btn bg" onClick={onClose}>âœ•</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {!isEdit && (
          <div>
            <label className="fl">Candidate</label>
            <select className="fi" value={form.candidate_id} onChange={e => setField("candidate_id", e.target.value)}>
              <option value="">Select candidate</option>
              {candidates.map(c => <option key={c.id} value={c.id}>{c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.username || `Candidate #${c.id}`}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="fl">Category</label>
          <select className="fi" value={form.category} onChange={e => setField("category", e.target.value)}>
            {["misconduct", "harassment", "fraud", "spam", "abuse", "policy", "other"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label className="fl">Status</label>
          <select className="fi" value={form.status} onChange={e => setField("status", e.target.value)}>
            {["open", "pending", "in_review", "resolved", "hidden"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 18, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13 }}>
            <input type="checkbox" checked={Boolean(form.visible_on_profile)} onChange={e => setField("visible_on_profile", e.target.checked)} />
            Visible on profile
          </label>
          {!isEdit && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13 }}>
              <input type="checkbox" checked={Boolean(form.terms_accepted)} onChange={e => setField("terms_accepted", e.target.checked)} />
              Accept terms
            </label>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="fl">Title</label>
        <input className="fi" value={form.title} onChange={e => setField("title", e.target.value)} placeholder="Complaint title" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="fl">Message</label>
        <textarea className="fi" rows={5} value={form.message} onChange={e => setField("message", e.target.value)} placeholder="Explain the issue in detail" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label className="fl">Hidden reason</label>
          <input className="fi" value={form.hidden_reason} onChange={e => setField("hidden_reason", e.target.value)} placeholder="Why is this hidden?" />
        </div>
        <div>
          <label className="fl">Admin note</label>
          <input className="fi" value={form.admin_note} onChange={e => setField("admin_note", e.target.value)} placeholder="Internal note" />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label className="fl">Proof upload</label>
        <input className="fi" type="file" onChange={e => setFile(e.target.files?.[0] || null)} />
        {(isEdit ? complaint?.proof_name : file?.name) && (
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6 }}>
            Selected: {isEdit ? complaint?.proof_name : file?.name}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button className="btn bo" onClick={onClose}>Cancel</button>
        <button className="btn bp" onClick={submit} disabled={busy}>{busy ? "Saving..." : (isEdit ? "Save Complaint" : "Create Complaint")}</button>
      </div>
    </Modal>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   PAGE: COMPLAINTS
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function ComplaintsPage({ complaints, summary, candidates, onSave, onDelete, onCreate, onRefresh, flash }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState(null);
  const [creator, setCreator] = useState(false);
  const [mode, setMode] = useState("candidate");
  const PER = 12;

  const q = search.toLowerCase();
  const list = complaints.filter((c) => {
    const match =
      !q ||
      (c.candidate_name || "").toLowerCase().includes(q) ||
      (c.candidate_email || "").toLowerCase().includes(q) ||
      (c.recruiter_name || "").toLowerCase().includes(q) ||
      (c.recruiter_company || "").toLowerCase().includes(q) ||
      (c.title || "").toLowerCase().includes(q) ||
      (c.message || "").toLowerCase().includes(q);
    const status = String(c.status || "open").toLowerCase();
    const isTargetMatch = mode === "candidate"
      ? Boolean(c.candidate_name || c.candidate_id)
      : Boolean(c.recruiter_name || c.recruiter_company);
    return isTargetMatch && match && (filter === "all" || status === filter);
  });

  const pages = Math.max(1, Math.ceil(list.length / PER));
  const paged = list.slice((page - 1) * PER, page * PER);

  const openEditor = (item) => setDraft(item);

  const saveDraft = async (payload) => {
    await onSave?.(payload);
    onRefresh?.();
  };

  const createComplaint = async (payload) => {
    await onCreate?.(payload);
    flash?.("Complaint created");
    onRefresh?.();
    setCreator(false);
  };

  const statusCounts = {
    all: complaints.length,
    open: summary?.open || 0,
    in_review: summary?.in_review || 0,
    resolved: summary?.resolved || 0,
    hidden: summary?.hidden || 0,
  };

  return (
    <div className="sf">
      {creator && <ComplaintEditorModal complaint={null} candidates={candidates} onClose={() => setCreator(false)} onCreate={createComplaint} flash={flash} />}
      {draft && <ComplaintEditorModal complaint={draft} candidates={candidates} onClose={() => setDraft(null)} onSave={saveDraft} flash={flash} />}

      <div className="stat-row">
        <StatCard icon="🧾" label="Total Complaints" value={statusCounts.all} color="#dc2626" />
        <StatCard icon="🟠" label="Open" value={statusCounts.open} color="#263012" />
        <StatCard icon="🔎" label="In Review" value={statusCounts.in_review} color="#0284c7" />
        <StatCard icon="✅" label="Resolved" value={statusCounts.resolved} color="#16a34a" />
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 16, display: "flex", gap: 10, background: "var(--brand-light)" }}>
        <button className={`btn ${mode === "candidate" ? "bp" : "bo"}`} onClick={() => setMode("candidate")} style={{ flex: 1 }}>🛡️ Reports on Candidates</button>
        <button className={`btn ${mode === "recruiter" ? "bp" : "bo"}`} onClick={() => setMode("recruiter")} style={{ flex: 1 }}>🏢 Reports on Recruiters</button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search candidate, recruiter, title, message..." />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["all", "open", "pending", "in_review", "resolved", "hidden"].map((k) => (
            <button key={k} className={`tab ${filter === k ? "on" : ""}`} onClick={() => { setFilter(k); setPage(1); }}>
              {k.replace("_", " ")}
            </button>
          ))}
        </div>
        <button className="btn bp" style={{ marginLeft: "auto" }} onClick={() => setCreator(true)}>➕ Log Admin Complaint</button>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "1.7fr 1.3fr 1fr 2.1fr 1fr 120px 120px" }}>
          <span>Candidate</span><span>Recruiter</span><span>Status</span><span>Title</span><span>Visibility</span><span>Created</span><span></span>
        </div>
        {paged.map((c) => (
          <div key={c.id} className="tr" style={{ gridTemplateColumns: "1.7fr 1.3fr 1fr 2.1fr 1fr 120px 120px", alignItems: "start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{c.candidate_name || "Candidate"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.candidate_email || c.candidate_username || "â€”"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{c.recruiter_company || c.recruiter_name || "â€”"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.recruiter_email || "â€”"}</div>
            </div>
            <StatusChip status={c.status} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{c.title || "Complaint"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{(c.category || "misconduct").replace("_", " ")}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: c.visible_on_profile ? "#16a34a" : "#263012" }}>{c.visible_on_profile ? "Visible" : "Hidden"}</div>
              {c.hidden_reason && <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.hidden_reason}</div>}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(c.created_at)}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="btn bp" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => openEditor(c)}>Edit</button>
              <button className="btn bo" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onDelete?.(c.id)}>Delete</button>
            </div>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No complaints found</div>}
      </div>

      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   PAGE: ACTIVITY
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function ActivityPage({ items, counts }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [userType, setUserType] = useState("all");
  const PER = 16;

  const q = search.toLowerCase();
  const list = items.filter((item) => {
    const match =
      !q ||
      (item.title || "").toLowerCase().includes(q) ||
      (item.message || "").toLowerCase().includes(q) ||
      (item.section || "").toLowerCase().includes(q) ||
      JSON.stringify(item.meta || {}).toLowerCase().includes(q);
    const candidateSections = ["complaints", "reviews", "candidates"];
    const recruiterSections = ["jobs", "transactions", "access", "database"];
    const typeMatch =
      userType === "all" ||
      (userType === "candidate" && candidateSections.includes(item.section)) ||
      (userType === "recruiter" && recruiterSections.includes(item.section));
    return typeMatch && match && (filter === "all" || item.section === filter);
  });

  const pages = Math.max(1, Math.ceil(list.length / PER));
  const paged = list.slice((page - 1) * PER, page * PER);

  const cardData = [
    ["reviews", "Reviews"],
    ["complaints", "Complaints"],
    ["jobs", "Jobs"],
    ["transactions", "Transactions"],
    ["access", "Access"],
    ["database", "Uploads"],
  ];

  return (
    <div className="sf">
      <div className="stat-row">
        {cardData.map(([key, label]) => (
          <StatCard key={key} icon="•" label={label} value={fmt(counts?.[key] || 0)} color={key === "complaints" ? "#dc2626" : key === "transactions" ? "#16a34a" : key === "access" ? "#2563eb" : "#1d240e"} />
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button className={`btn ${userType === "all" ? "bp" : "bo"}`} onClick={() => setUserType("all")}>🌐 All Activity</button>
        <button className={`btn ${userType === "candidate" ? "bp" : "bo"}`} onClick={() => setUserType("candidate")}>👥 Candidate Side</button>
        <button className={`btn ${userType === "recruiter" ? "bp" : "bo"}`} onClick={() => setUserType("recruiter")}>🤝 Recruiter Side</button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search activity..." />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["all", "reviews", "complaints", "jobs", "transactions", "access", "candidates", "database"].map((k) => (
            <button key={k} className={`tab ${filter === k ? "on" : ""}`} onClick={() => { setFilter(k); setPage(1); }}>
              {k}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} items</span>
      </div>

      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "54px 1.2fr 2fr 1fr 110px" }}>
          <span></span><span>Item</span><span>Details</span><span>Type</span><span>Time</span>
        </div>
        {paged.map((item) => (
          <div key={item.id} className="tr" style={{ gridTemplateColumns: "54px 1.2fr 2fr 1fr 110px", alignItems: "start" }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: item.section === "complaints" ? "#fee2e2" : item.section === "reviews" ? "#fef3c7" : item.section === "jobs" ? "#dbeafe" : item.section === "transactions" ? "#dcfce7" : item.section === "access" ? "#ede9fe" : "#f0f2f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {item.section === "complaints" ? "🧾" : item.section === "reviews" ? "⭐" : item.section === "jobs" ? "🏢" : item.section === "transactions" ? "💳" : item.section === "access" ? "🔐" : "📎"}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{item.section}</div>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.5 }}>
              {item.message}
              {item.meta && Object.keys(item.meta).length > 0 && (
                <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-3)" }}>
                  {Object.entries(item.meta).map(([k, v]) => `${k}: ${String(v)}`).join(" | ")}
                </div>
              )}
            </div>
            <StatusChip status={item.meta?.status || item.section} />
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(item.created_at)}</div>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No activity found</div>}
      </div>

      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}

function FraudReportsPage({ reports, flash }) {
  return (
    <div className="sf">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontWeight: 800 }}>🛡️ Fraud & Scam Reports</h2>
        <p style={{ fontSize: 13, color: "var(--text-3)" }}>Reports submitted by users via the Report Fraud page.</p>
      </div>
      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "1fr 1fr 2fr 1fr 120px" }}>
          <span>Reporter</span><span>Target</span><span>Reason / Details</span><span>Status</span><span>Date</span>
        </div>
        {reports.map(r => (
          <div key={r.id} className="tr" style={{ gridTemplateColumns: "1fr 1fr 2fr 1fr 120px", alignItems: "start" }}>
            <span style={{ fontSize: 13 }}>{r.reporter_role}</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{r.target_type} (#{r.target_id})</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{r.reason}</div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 4 }}>{r.details}</div>
            </div>
            <StatusChip status={r.status} />
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(r.created_at)}</span>
          </div>
        ))}
        {!reports.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No fraud reports found</div>}
      </div>
    </div>
  );
}

function SupportPage({ fraudReports, contactMessages, onReadMessage, flash }) {
  const [tab, setTab] = useState("contact");

  return (
    <div className="sf">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontWeight: 800, fontSize: 22 }}>🎧 Support Center</h2>
        <p style={{ fontSize: 13, color: "var(--text-3)" }}>
          Manage contact form submissions and fraud reports from users.
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <div className="sc">
          <div style={{ fontSize: 22, marginBottom: 8 }}>✉️</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0284c7" }}>{contactMessages.length}</div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>Total Inquiries</div>
        </div>
        <div className="sc">
          <div style={{ fontSize: 22, marginBottom: 8 }}>🔴</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#dc2626" }}>
            {contactMessages.filter(m => !m.is_read).length}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>Unread Messages</div>
        </div>
        <div className="sc">
          <div style={{ fontSize: 22, marginBottom: 8 }}>🛡️</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#7c3aed" }}>{fraudReports.length}</div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>Fraud Reports</div>
        </div>
        <div className="sc">
          <div style={{ fontSize: 22, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#263012" }}>
            {fraudReports.filter(r => r.status === "open" || !r.status).length}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>Open Fraud Cases</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button
          className={`btn ${tab === "contact" ? "bp" : "bo"}`}
          onClick={() => setTab("contact")}
        >
          ✉️ Contact Inquiries
          {contactMessages.filter(m => !m.is_read).length > 0 && (
            <span style={{
              marginLeft: 8, background: "#dc2626", color: "#fff",
              borderRadius: 99, fontSize: 10, fontWeight: 800,
              padding: "1px 7px"
            }}>
              {contactMessages.filter(m => !m.is_read).length} new
            </span>
          )}
        </button>
        <button
          className={`btn ${tab === "fraud" ? "bp" : "bo"}`}
          onClick={() => setTab("fraud")}
        >
          🛡️ Fraud Reports
          {fraudReports.filter(r => r.status === "open" || !r.status).length > 0 && (
            <span style={{
              marginLeft: 8, background: "#7c3aed", color: "#fff",
              borderRadius: 99, fontSize: 10, fontWeight: 800,
              padding: "1px 7px"
            }}>
              {fraudReports.filter(r => r.status === "open" || !r.status).length} open
            </span>
          )}
        </button>
      </div>

      {/* Contact Messages Tab */}
      {tab === "contact" && (
        <div className="card">
          {contactMessages.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>
              No contact messages yet
            </div>
          )}
          {contactMessages.map(m => (
            <div
              key={m.id}
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid var(--border-light)",
                opacity: m.is_read ? 0.6 : 1,
                background: m.is_read ? "transparent" : "var(--brand-light)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Av name={m.name} size={36} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{m.email}</div>
                  </div>
                  {!m.is_read && (
                    <span style={{
                      background: "#dc2626", color: "#fff", borderRadius: 99,
                      fontSize: 10, fontWeight: 800, padding: "2px 8px"
                    }}>NEW</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>{fmtDate(m.created_at)}</span>
                  {m.dept && (
                    <span style={{
                      background: "var(--surface-alt)", border: "1.5px solid var(--border-light)",
                      borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "3px 10px",
                      color: "var(--text-2)", textTransform: "capitalize"
                    }}>{m.dept}</span>
                  )}
                </div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: "var(--text-1)" }}>
                📌 {m.subject}
              </div>
              <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.7, marginBottom: 10 }}>
                {m.message}
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                {!m.is_read && (
                  <button
                    className="btn bs"
                    style={{ padding: "5px 14px", fontSize: 12 }}
                    onClick={() => onReadMessage(m.id)}
                  >
                    ✓ Mark as Read
                  </button>
                )}
                {m.email && (
                  <a
                    href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "")}`}
                    className="btn bo"
                    style={{ padding: "5px 14px", fontSize: 12, textDecoration: "none" }}
                  >
                    ↩ Reply via Email
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fraud Reports Tab */}
      {tab === "fraud" && (
        <div className="card">
          <div className="th" style={{ gridTemplateColumns: "1fr 1.2fr 1.5fr 2fr 1fr 120px" }}>
            <span>Reporter</span>
            <span>Target</span>
            <span>Category</span>
            <span>Details</span>
            <span>Status</span>
            <span>Date</span>
          </div>
          {fraudReports.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>
              No fraud reports yet
            </div>
          )}
          {fraudReports.map(r => (
            <div
              key={r.id}
              className="tr"
              style={{ gridTemplateColumns: "1fr 1.2fr 1.5fr 2fr 1fr 120px", alignItems: "start" }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name || "Anonymous"}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{r.email || r.reporter_role || "—"}</div>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {r.target_type ? `${r.target_type} #${r.target_id}` : r.company || "—"}
                </div>
                {r.job_ref && (
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>Job: {r.job_ref}</div>
                )}
              </div>
              <div>
                <span style={{
                  background: r.priority === "high" ? "#fee2e2" : r.priority === "medium" ? "#fef3c7" : "#f0f2f6",
                  color: r.priority === "high" ? "#dc2626" : r.priority === "medium" ? "#263012" : "#6b7280",
                  padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 800
                }}>
                  {r.priority ? `${r.priority.toUpperCase()} PRIORITY` : "—"}
                </span>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4, textTransform: "capitalize" }}>
                  {r.category || r.reason || "—"}
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.6 }}>
                {(r.description || r.details || "No details provided").slice(0, 140)}
                {(r.description || r.details || "").length > 140 ? "…" : ""}
              </div>
              <StatusChip status={r.status || "open"} />
              <span style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(r.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
};
function HRTalkAdminPage({ overview, experts, plans, slots, onReload, flash }) {
  const [saving, setSaving] = useState(false);
  const [expertDraft, setExpertDraft] = useState({ name: "", role: "", email: "", status: "online" });
  const [planDraft, setPlanDraft] = useState({ plan_code: "", plan_name: "", price_inr: 0, duration_mins: 30 });
  const [slotDraft, setSlotDraft] = useState({ label: "", start_time: "09:00" });

  const run = async (fn, ok = "Updated") => {
    setSaving(true);
    try {
      await fn();
      await onReload();
      flash(ok);
    } catch (err) {
      flash(err?.response?.data?.error || err?.message || "Failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const summary = overview?.summary || {};

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="grid g4">
        <StatCard icon="📅" label="Today's Sessions" value={summary.total_sessions || 0} color="#1d240e" />
        <StatCard icon="👥" label="Unique Candidates" value={summary.unique_candidates || 0} color="#0284c7" />
        <StatCard icon="💳" label="Paid Sessions" value={summary.paid_sessions || 0} color="#16a34a" />
        <StatCard icon="₹" label="Paid Revenue" value={fmtMoney(summary.paid_revenue || 0)} color="#7c3aed" />
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 800 }}>Today's HRTalk Candidates</div>
          <button className="btn bo" onClick={onReload} disabled={saving}>Refresh</button>
        </div>
        <div className="table">
          <div className="tr th" style={{ gridTemplateColumns: "1.2fr 1fr .8fr .7fr .7fr" }}>
            <div>Candidate</div><div>Expert</div><div>Plan</div><div>Status</div><div>Amount</div>
          </div>
          {(overview?.today_candidates || []).map((row) => (
            <div key={row.id} className="tr" style={{ gridTemplateColumns: "1.2fr 1fr .8fr .7fr .7fr" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{row.candidate_name || "Candidate"}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{row.candidate_email || "—"}</div>
              </div>
              <div>{row.hr_name || "—"}</div>
              <div>{row.plan_name || "—"}</div>
              <StatusChip status={row.payment_status || "pending"} />
              <div>{fmtMoney(row.plan_price_inr || 0)}</div>
            </div>
          ))}
          {!(overview?.today_candidates || []).length && <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)" }}>No sessions today</div>}
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Daily HRTalk Usage (Last 14 Days)</div>
        <div className="table">
          <div className="tr th" style={{ gridTemplateColumns: "1fr .8fr .8fr .8fr .8fr" }}>
            <div>Date</div><div>Sessions</div><div>Candidates</div><div>Paid</div><div>Revenue</div>
          </div>
          {(overview?.daily || []).map((row) => (
            <div key={row.day} className="tr" style={{ gridTemplateColumns: "1fr .8fr .8fr .8fr .8fr" }}>
              <div>{fmtDate(row.day)}</div>
              <div>{fmt(row.sessions || 0)}</div>
              <div>{fmt(row.candidates || 0)}</div>
              <div>{fmt(row.paid_sessions || 0)}</div>
              <div>{fmtMoney(row.revenue || 0)}</div>
            </div>
          ))}
          {!(overview?.daily || []).length && <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)" }}>No daily usage available yet</div>}
        </div>
      </div>

      <div className="grid g2">
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Experts</div>
          <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
            <input className="fi" placeholder="Name" value={expertDraft.name} onChange={(e) => setExpertDraft((p) => ({ ...p, name: e.target.value }))} />
            <input className="fi" placeholder="Role" value={expertDraft.role} onChange={(e) => setExpertDraft((p) => ({ ...p, role: e.target.value }))} />
            <input className="fi" placeholder="Email" value={expertDraft.email} onChange={(e) => setExpertDraft((p) => ({ ...p, email: e.target.value }))} />
            <input className="fi" placeholder="Status: online/away/busy" value={expertDraft.status} onChange={(e) => setExpertDraft((p) => ({ ...p, status: e.target.value }))} />
            <button className="btn bp" disabled={saving} onClick={() => run(() => axios.post(`${HRTALK_ADMIN_API}/experts`, expertDraft, { headers: authHdr() }), "Expert created")}>Add Expert</button>
          </div>
          <div className="table">
            <div className="tr th" style={{ gridTemplateColumns: "1fr 1fr .8fr" }}><div>Name</div><div>Role</div><div>Actions</div></div>
            {experts.map((item) => (
              <div key={item.id} className="tr" style={{ gridTemplateColumns: "1fr 1fr .8fr" }}>
                <div>
                  {item.name}
                  <span style={{ marginLeft: 8 }}><StatusChip status={Number(item.is_active) === 1 ? "active" : "inactive"} /></span>
                </div>
                <div>{item.role}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn bo" onClick={() => run(async () => {
                    const name = window.prompt("Expert name", item.name);
                    if (!name) return;
                    const role = window.prompt("Role", item.role || "");
                    if (!role) return;
                    await axios.put(`${HRTALK_ADMIN_API}/experts/${item.id}`, { ...item, name, role }, { headers: authHdr() });
                  }, "Expert updated")}>Edit</button>
                  <button className="btn bo" onClick={() => run(() => axios.put(`${HRTALK_ADMIN_API}/experts/${item.id}`, { ...item, is_active: Number(item.is_active) === 1 ? 0 : 1 }, { headers: authHdr() }), Number(item.is_active) === 1 ? "Expert hidden" : "Expert shown")}>
                    {Number(item.is_active) === 1 ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Plans</div>
          <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
            <input className="fi" placeholder="Code" value={planDraft.plan_code} onChange={(e) => setPlanDraft((p) => ({ ...p, plan_code: e.target.value }))} />
            <input className="fi" placeholder="Name" value={planDraft.plan_name} onChange={(e) => setPlanDraft((p) => ({ ...p, plan_name: e.target.value }))} />
            <input className="fi" placeholder="Price INR" value={planDraft.price_inr} onChange={(e) => setPlanDraft((p) => ({ ...p, price_inr: e.target.value }))} />
            <input className="fi" placeholder="Duration Mins" value={planDraft.duration_mins} onChange={(e) => setPlanDraft((p) => ({ ...p, duration_mins: e.target.value }))} />
            <button className="btn bp" disabled={saving} onClick={() => run(() => axios.post(`${HRTALK_ADMIN_API}/plans`, planDraft, { headers: authHdr() }), "Plan created")}>Add Plan</button>
          </div>
          <div className="table">
            <div className="tr th" style={{ gridTemplateColumns: "1fr .8fr .8fr" }}><div>Plan</div><div>Price</div><div>Actions</div></div>
            {plans.map((item) => (
              <div key={item.id} className="tr" style={{ gridTemplateColumns: "1fr .8fr .8fr" }}>
                <div>
                  {item.plan_name} ({item.plan_code})
                  <span style={{ marginLeft: 8 }}><StatusChip status={Number(item.is_active) === 1 ? "active" : "inactive"} /></span>
                </div>
                <div>{fmtMoney(item.price_inr || 0)}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn bo" onClick={() => run(async () => {
                    const price = window.prompt("Price INR", String(item.price_inr || 0));
                    if (price == null) return;
                    await axios.put(`${HRTALK_ADMIN_API}/plans/${item.id}`, { ...item, price_inr: Number(price) }, { headers: authHdr() });
                  }, "Plan updated")}>Edit</button>
                  <button className="btn bo" onClick={() => run(() => axios.put(`${HRTALK_ADMIN_API}/plans/${item.id}`, { ...item, is_active: Number(item.is_active) === 1 ? 0 : 1 }, { headers: authHdr() }), Number(item.is_active) === 1 ? "Plan hidden" : "Plan shown")}>
                    {Number(item.is_active) === 1 ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Slot Templates</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 10 }}>
          <input className="fi" placeholder="Label" value={slotDraft.label} onChange={(e) => setSlotDraft((p) => ({ ...p, label: e.target.value }))} />
          <input className="fi" placeholder="Start Time HH:MM" value={slotDraft.start_time} onChange={(e) => setSlotDraft((p) => ({ ...p, start_time: e.target.value }))} />
          <button className="btn bp" disabled={saving} onClick={() => run(() => axios.post(`${HRTALK_ADMIN_API}/slots`, slotDraft, { headers: authHdr() }), "Slot created")}>Add Slot</button>
        </div>
        <div className="table">
          <div className="tr th" style={{ gridTemplateColumns: "1fr 1fr .8fr" }}><div>Label</div><div>Start Time</div><div>Actions</div></div>
          {slots.map((item) => (
            <div key={item.id} className="tr" style={{ gridTemplateColumns: "1fr 1fr .8fr" }}>
              <div>
                {item.label}
                <span style={{ marginLeft: 8 }}><StatusChip status={Number(item.is_active) === 1 ? "active" : "inactive"} /></span>
              </div>
              <div>{String(item.start_time || "").slice(0, 5)}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn bo" onClick={() => run(async () => {
                  const start = window.prompt("Start time HH:MM", String(item.start_time || "").slice(0, 5));
                  if (!start) return;
                  await axios.put(`${HRTALK_ADMIN_API}/slots/${item.id}`, { ...item, start_time: start }, { headers: authHdr() });
                }, "Slot updated")}>Edit</button>
                <button className="btn bo" onClick={() => run(() => axios.put(`${HRTALK_ADMIN_API}/slots/${item.id}`, { ...item, is_active: Number(item.is_active) === 1 ? 0 : 1 }, { headers: authHdr() }), Number(item.is_active) === 1 ? "Slot hidden" : "Slot shown")}>
                  {Number(item.is_active) === 1 ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function CoursesAdminPage({ courses = [], instructors = [], onReload, flash }) {
  const emptyForm = {
    title: "",
    description: "",
    category: "Development",
    level: "beginner",
    price: "0",
    original_price: "0",
    duration: "",
    lessons: "0",
    tags: "",
    featured: false,
    banner: "",
    accent: "#6366f1",
    instructor_id: "",
    is_active: true,
  };
  const emptyVideoForm = {
    title: "",
    description: "",
    source_type: "upload",
    video_url: "",
    thumbnail: "",
    duration_seconds: "",
    notes: "",
    notes_pdf: "",
    sort_order: "",
    is_intro: false,
    is_active: true,
  };

  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [videosCourse, setVideosCourse] = useState(null);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosList, setVideosList] = useState([]);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [videoSaving, setVideoSaving] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [videoForm, setVideoForm] = useState(emptyVideoForm);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPdfFile, setVideoPdfFile] = useState(null);
  const [videoThumbFile, setVideoThumbFile] = useState(null);

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateVideoField = (key, value) => setVideoForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };
  const resetVideoEditor = () => {
    setEditingVideoId(null);
    setVideoForm(emptyVideoForm);
    setVideoFile(null);
    setVideoPdfFile(null);
    setVideoThumbFile(null);
  };

  const beginEdit = (course) => {
    setEditingId(course.id);
    setForm({
      title: course.title || "",
      description: course.description || "",
      category: course.category || "Development",
      level: course.level || "beginner",
      price: String(course.price ?? 0),
      original_price: String(course.original_price ?? 0),
      duration: course.duration || "",
      lessons: String(course.lessons ?? 0),
      tags: Array.isArray(course.tags) ? course.tags.join(", ") : "",
      featured: Boolean(course.featured),
      banner: course.banner || "",
      accent: course.accent || "#6366f1",
      instructor_id: course.instructor_id ? String(course.instructor_id) : "",
      is_active: Boolean(course.is_active),
    });
  };

  const fetchCourseVideos = useCallback(async (courseId) => {
    setVideosLoading(true);
    try {
      const { data } = await axios.get(`${API}/courses/${courseId}/videos`, { headers: authHdr() });
      setVideosList(Array.isArray(data?.videos) ? data.videos : []);
    } catch (err) {
      flash(err?.response?.data?.error || "Failed to load course videos", "error");
    } finally {
      setVideosLoading(false);
    }
  }, [flash]);

  const openVideosManager = async (course) => {
    setVideosCourse(course);
    setVideoEditorOpen(false);
    resetVideoEditor();
    await fetchCourseVideos(course.id);
  };

  const closeVideosManager = () => {
    setVideosCourse(null);
    setVideosList([]);
    setVideoEditorOpen(false);
    resetVideoEditor();
  };

  const openVideoAddPopup = () => {
    const hasIntro = videosList.some((item) => Number(item?.is_intro) === 1 || item?.is_intro === true);
    setEditingVideoId(null);
    setVideoForm({
      ...emptyVideoForm,
      description: "",
      sort_order: String(videosList.length + 1),
      is_intro: !hasIntro,
      is_active: true,
      source_type: "upload",
    });
    setVideoFile(null);
    setVideoPdfFile(null);
    setVideoThumbFile(null);
    setVideoEditorOpen(true);
  };

  const openVideoEditPopup = (video) => {
    setEditingVideoId(video.id);
    setVideoForm({
      title: String(video.title || ""),
      description: String(video.description || ""),
      source_type: String(video.video_url || "").startsWith("/uploads/course-videos/") ? "upload" : "link",
      video_url: String(video.video_url || ""),
      thumbnail: String(video.thumbnail || ""),
      duration_seconds: video.duration_seconds == null ? "" : String(video.duration_seconds),
      notes: String(video.notes || ""),
      notes_pdf: String(video.notes_pdf || ""),
      sort_order: video.sort_order == null ? "" : String(video.sort_order),
      is_intro: Number(video.is_intro) === 1 || video.is_intro === true,
      is_active: Number(video.is_active) !== 0 && video.is_active !== false,
    });
    setVideoFile(null);
    setVideoPdfFile(null);
    setVideoThumbFile(null);
    setVideoEditorOpen(true);
  };

  const deleteCourseVideo = async (videoId) => {
    if (!videosCourse?.id) return;
    if (!window.confirm("Delete this video from course playlist?")) return;
    try {
      await axios.delete(`${API}/courses/${videosCourse.id}/videos/${videoId}`, { headers: authHdr() });
      flash("Video removed from playlist");
      await fetchCourseVideos(videosCourse.id);
      onReload?.();
    } catch (err) {
      flash(err?.response?.data?.error || "Failed to delete video", "error");
    }
  };

  const saveCourseVideo = async () => {
    if (!videosCourse?.id) return;
    const title = String(videoForm.title || "").trim();
    const description = String(videoForm.description || "").trim();
    const videoUrl = String(videoForm.video_url || "").trim();
    if (!title) {
      flash("Video title is required", "error");
      return;
    }
    if (!description) {
      flash("Video description is required", "error");
      return;
    }
    if (!editingVideoId && !videoFile && !videoUrl) {
      flash("Upload a video file or provide a video link", "error");
      return;
    }
    if (videoForm.source_type === "link" && !videoUrl && !videoFile && !editingVideoId) {
      flash("Please enter video link", "error");
      return;
    }

    const payload = new FormData();
    payload.append("title", title);
    payload.append("description", description);
    if (videoUrl) payload.append("video_url", videoUrl);
    if (String(videoForm.thumbnail || "").trim()) payload.append("thumbnail", String(videoForm.thumbnail || "").trim());
    if (String(videoForm.duration_seconds || "").trim() !== "") payload.append("duration_seconds", String(videoForm.duration_seconds || "").trim());
    if (String(videoForm.notes || "") !== "") payload.append("notes", String(videoForm.notes || ""));
    if (String(videoForm.notes_pdf || "").trim()) payload.append("notes_pdf", String(videoForm.notes_pdf || "").trim());
    if (String(videoForm.sort_order || "").trim()) payload.append("sort_order", String(videoForm.sort_order || "").trim());
    payload.append("is_intro", videoForm.is_intro ? "1" : "0");
    payload.append("is_active", videoForm.is_active ? "1" : "0");
    if (videoFile) payload.append("video_file", videoFile);
    if (videoPdfFile) payload.append("notes_pdf_file", videoPdfFile);
    if (videoThumbFile) payload.append("thumbnail_file", videoThumbFile);

    setVideoSaving(true);
    try {
      if (editingVideoId) {
        await axios.put(`${API}/courses/${videosCourse.id}/videos/${editingVideoId}`, payload, { headers: authHdr() });
        flash("Video updated");
      } else {
        await axios.post(`${API}/courses/${videosCourse.id}/videos`, payload, { headers: authHdr() });
        flash("Video added to playlist");
      }
      setVideoEditorOpen(false);
      resetVideoEditor();
      await fetchCourseVideos(videosCourse.id);
      onReload?.();
    } catch (err) {
      flash(err?.response?.data?.error || "Failed to save video", "error");
    } finally {
      setVideoSaving(false);
    }
  };

  const buildPayload = () => ({
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim(),
    category: String(form.category || "Development").trim(),
    level: String(form.level || "beginner").trim(),
    price: Number(form.price || 0),
    original_price: Number(form.original_price || 0),
    duration: String(form.duration || "").trim(),
    lessons: Number(form.lessons || 0),
    tags: String(form.tags || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    featured: Boolean(form.featured),
    banner: String(form.banner || "").trim() || null,
    accent: String(form.accent || "#6366f1").trim() || "#6366f1",
    instructor_id: form.instructor_id ? Number(form.instructor_id) : null,
    is_active: Boolean(form.is_active),
  });

  const saveCourse = async () => {
    const payload = buildPayload();
    if (!payload.title) {
      flash("Course title is required", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/courses/${editingId}`, payload, { headers: authHdr() });
        flash("Course updated");
      } else {
        await axios.post(`${API}/courses`, payload, { headers: authHdr() });
        flash("Course created");
      }
      resetForm();
      onReload?.();
    } catch (err) {
      flash(err?.response?.data?.error || "Failed to save course", "error");
    } finally {
      setSaving(false);
    }
  };

  const removeCourse = async (id) => {
    if (!window.confirm("Archive this course?")) return;
    try {
      await axios.delete(`${API}/courses/${id}`, { headers: authHdr() });
      flash("Course archived");
      onReload?.();
    } catch (err) {
      flash(err?.response?.data?.error || "Failed to archive course", "error");
    }
  };

  const filteredCourses = courses.filter((course) => {
    const text = `${course.title || ""} ${course.category || ""} ${course.level || ""}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });

  return (
    <div className="sf">
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ fontSize: 17, fontWeight: 800 }}>Course Manager</h3>
          <button className="btn bo" onClick={onReload}>Refresh</button>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
          <input className="fi" placeholder="Course title" value={form.title} onChange={(e) => updateField("title", e.target.value)} />
          <input className="fi" placeholder="Category" value={form.category} onChange={(e) => updateField("category", e.target.value)} />
          <select className="fi" value={form.level} onChange={(e) => updateField("level", e.target.value)}>
            <option value="beginner">beginner</option>
            <option value="intermediate">intermediate</option>
            <option value="advanced">advanced</option>
            <option value="expert">expert</option>
          </select>
          <input className="fi" placeholder="Price" type="number" value={form.price} onChange={(e) => updateField("price", e.target.value)} />
          <input className="fi" placeholder="Original Price" type="number" value={form.original_price} onChange={(e) => updateField("original_price", e.target.value)} />
          <input className="fi" placeholder="Duration (e.g. 8h 30m)" value={form.duration} onChange={(e) => updateField("duration", e.target.value)} />
          <input className="fi" placeholder="Lessons" type="number" value={form.lessons} onChange={(e) => updateField("lessons", e.target.value)} />
          <input className="fi" placeholder="Banner URL" value={form.banner} onChange={(e) => updateField("banner", e.target.value)} />
          <input className="fi" placeholder="Accent color (#6366f1)" value={form.accent} onChange={(e) => updateField("accent", e.target.value)} />
          <input className="fi" placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => updateField("tags", e.target.value)} />
          <select className="fi" value={form.instructor_id} onChange={(e) => updateField("instructor_id", e.target.value)}>
            <option value="">No instructor</option>
            {instructors.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.name} {inst.title ? `(${inst.title})` : ""}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={form.featured} onChange={(e) => updateField("featured", e.target.checked)} />
              Featured
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => updateField("is_active", e.target.checked)} />
              Active
            </label>
          </div>
        </div>

        <textarea
          className="fi"
          style={{ marginTop: 10, minHeight: 90 }}
          placeholder="Description"
          value={form.description}
          onChange={(e) => updateField("description", e.target.value)}
        />

        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button className="btn bp" disabled={saving} onClick={saveCourse}>
            {saving ? "Saving..." : editingId ? "Update Course" : "Create Course"}
          </button>
          {editingId && (
            <button className="btn bo" onClick={resetForm}>Cancel Edit</button>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800 }}>All Courses ({filteredCourses.length})</h3>
          <Search value={query} onChange={setQuery} placeholder="Search courses..." />
        </div>

        <div className="table">
          <div className="tr th" style={{ gridTemplateColumns: "2fr 1fr .7fr .7fr .7fr 1fr" }}>
            <div>Course</div>
            <div>Category</div>
            <div>Price</div>
            <div>Rating</div>
            <div>Status</div>
            <div>Actions</div>
          </div>
          {filteredCourses.map((course) => (
            <div key={course.id} className="tr" style={{ gridTemplateColumns: "2fr 1fr .7fr .7fr .7fr 1fr" }}>
              <div>
                <div style={{ fontWeight: 700 }}>{course.title}</div>
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                  {course.level} • {course.lessons} lessons • {course.enrolled || 0} enrolled
                </div>
              </div>
              <div>{course.category}</div>
              <div>{fmtMoney(course.price || 0)}</div>
              <div>{Number(course.rating || 0).toFixed(1)} ({course.reviews_count || 0})</div>
              <div>
                <StatusChip status={course.is_active ? "active" : "inactive"} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn bo" onClick={() => beginEdit(course)}>Edit</button>
                <button className="btn bo" onClick={() => openVideosManager(course)}>Playlist</button>
                <button className="btn bd" onClick={() => removeCourse(course.id)}>Archive</button>
              </div>
            </div>
          ))}
          {!filteredCourses.length && (
            <div className="tr" style={{ display: "block", textAlign: "center", color: "var(--text-3)" }}>
              No courses found
            </div>
          )}
        </div>
      </div>

      {videosCourse && (
        <Modal onClose={closeVideosManager} width={1080}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14, paddingBottom: 14, borderBottom: "1.5px solid var(--border-light)" }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
                Course Playlist
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-2)" }}>
                {videosCourse.title} - upload unlimited videos or add links, with description and intro video support.
              </p>
            </div>
            <button className="btn bo" onClick={openVideoAddPopup}>+ Add Video</button>
            <button className="btn bg" onClick={closeVideosManager} style={{ fontSize: 18, padding: "4px 8px" }}>✕</button>
          </div>

          <div style={{ display: "grid", gap: 10, maxHeight: "62vh", overflowY: "auto" }}>
            {videosLoading && (
              <div className="card" style={{ padding: 14, color: "var(--text-3)", fontWeight: 600 }}>
                Loading videos...
              </div>
            )}

            {!videosLoading && videosList.map((video, idx) => (
              <div key={video.id} className="card" style={{ padding: 14, border: Number(video.is_intro) === 1 || video.is_intro === true ? "1.5px solid #16a34a55" : undefined }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 800 }}>{idx + 1}. {video.title || "Untitled Video"}</span>
                      {(Number(video.is_intro) === 1 || video.is_intro === true) && <StatusChip status="intro" />}
                      {(Number(video.is_active) === 1 || video.is_active === true) ? <StatusChip status="active" /> : <StatusChip status="inactive" />}
                    </div>
                    <div style={{ color: "var(--text-3)", fontSize: 12, marginTop: 4 }}>
                      Sort: {video.sort_order || "-"} | Duration: {video.duration_seconds || "-"} sec
                    </div>
                    <div style={{ marginTop: 8, color: "var(--text-2)", fontSize: 13, whiteSpace: "pre-wrap" }}>
                      {video.description || "No description"}
                    </div>
                    {video.video_url && (
                      <a href={video.video_url} target="_blank" rel="noreferrer" style={{ marginTop: 8, display: "inline-block", fontSize: 12 }}>
                        Open Video
                      </a>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn bo" onClick={() => openVideoEditPopup(video)}>Edit</button>
                    <button className="btn bd" onClick={() => deleteCourseVideo(video.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}

            {!videosLoading && !videosList.length && (
              <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--text-3)" }}>
                No videos in this playlist yet.
              </div>
            )}
          </div>
        </Modal>
      )}

      {videosCourse && videoEditorOpen && (
        <Modal onClose={() => { setVideoEditorOpen(false); resetVideoEditor(); }} width={820}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: "1.5px solid var(--border-light)" }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, flex: 1 }}>
              {editingVideoId ? "Edit Course Video" : "Add New Course Video"}
            </h3>
            <button className="btn bg" onClick={() => { setVideoEditorOpen(false); resetVideoEditor(); }} style={{ fontSize: 18, padding: "4px 8px" }}>✕</button>
          </div>

          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
            <input className="fi" placeholder="Video title" value={videoForm.title} onChange={(e) => updateVideoField("title", e.target.value)} />
            <input className="fi" placeholder="Sort order" type="number" value={videoForm.sort_order} onChange={(e) => updateVideoField("sort_order", e.target.value)} />
            <input className="fi" placeholder="Duration (seconds)" type="number" value={videoForm.duration_seconds} onChange={(e) => updateVideoField("duration_seconds", e.target.value)} />

            <select className="fi" value={videoForm.source_type} onChange={(e) => updateVideoField("source_type", e.target.value)}>
              <option value="upload">Upload video file</option>
              <option value="link">Use video link</option>
            </select>

            <input className="fi" placeholder="Video link (YouTube/hosted URL)" value={videoForm.video_url} onChange={(e) => updateVideoField("video_url", e.target.value)} />
            <input className="fi" placeholder="Thumbnail link (optional)" value={videoForm.thumbnail} onChange={(e) => updateVideoField("thumbnail", e.target.value)} />
            <input className="fi" placeholder="PDF link (optional)" value={videoForm.notes_pdf} onChange={(e) => updateVideoField("notes_pdf", e.target.value)} />

            <div style={{ display: "grid", gap: 6 }}>
              <label className="fl" style={{ marginBottom: 0 }}>Upload video file</label>
              <input className="fi" type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label className="fl" style={{ marginBottom: 0 }}>Upload notes PDF</label>
              <input className="fi" type="file" accept="application/pdf" onChange={(e) => setVideoPdfFile(e.target.files?.[0] || null)} />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label className="fl" style={{ marginBottom: 0 }}>Upload thumbnail image</label>
              <input className="fi" type="file" accept="image/*" onChange={(e) => setVideoThumbFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          <textarea
            className="fi"
            style={{ marginTop: 10, minHeight: 90 }}
            placeholder="Video description (required)"
            value={videoForm.description}
            onChange={(e) => updateVideoField("description", e.target.value)}
          />
          <textarea
            className="fi"
            style={{ marginTop: 10, minHeight: 90 }}
            placeholder="Notes (optional)"
            value={videoForm.notes}
            onChange={(e) => updateVideoField("notes", e.target.value)}
          />

          <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={videoForm.is_intro} onChange={(e) => updateVideoField("is_intro", e.target.checked)} />
              Mark as Introduction Video (auto-opens for users)
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={videoForm.is_active} onChange={(e) => updateVideoField("is_active", e.target.checked)} />
              Active
            </label>
          </div>

          <div style={{ marginTop: 12, color: "var(--text-3)", fontSize: 12 }}>
            You can upload video files directly or provide external links. Description is required so every playlist video shows proper details on course page.
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn bo" onClick={() => { setVideoEditorOpen(false); resetVideoEditor(); }}>
              Cancel
            </button>
            <button className="btn bp" disabled={videoSaving} onClick={saveCourseVideo}>
              {videoSaving ? "Saving..." : editingVideoId ? "Update Video" : "Add Video"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function GraphAnalyticsPage() {
  return <RealtimeDashboard />;
}

export default function TalentHiveAdmin() {
  const [page, setPage] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [toast, setToast] = useState({ msg: "", type: "success" });

  const [stats, setStats] = useState({});
  const [recruiters, setRecruiters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ total: 0, pending: 0, visible: 0, hidden: 0, removal_requested: 0 });
  const [complaints, setComplaints] = useState([]);
  const [complaintSummary, setComplaintSummary] = useState({ total: 0, open: 0, in_review: 0, resolved: 0, hidden: 0 });
  const [activity, setActivity] = useState([]);
  const [activityCounts, setActivityCounts] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [fraudReports, setFraudReports] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [hrtalkOverview, setHrtalkOverview] = useState({ summary: {}, today_candidates: [], daily: [] });
  const [hrtalkExperts, setHrtalkExperts] = useState([]);
  const [hrtalkPlans, setHrtalkPlans] = useState([]);
  const [hrtalkSlots, setHrtalkSlots] = useState([]);
  const [adminCourses, setAdminCourses] = useState([]);
  const [courseInstructors, setCourseInstructors] = useState([]);

  const [viewRecruiter, setViewRecruiter] = useState(null);
  const [assignPlan, setAssignPlan] = useState(null);
  const [viewJob, setViewJob] = useState(null);
  const [viewCandidate, setViewCandidate] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewReview, setViewReview] = useState(null);

  const flash = useCallback((msg, type = "success") => setToast({ msg, type }), []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [sR, rR, jR, cR, tR, revR, compR, actR, anR, fR, mR, hoR, heR, hpR, hsR, crR, insR] = await Promise.allSettled([
        axios.get(`${API}/dashboard`, { headers: authHdr() }),
        axios.get(`${API}/recruiters`, { headers: authHdr() }),
        axios.get(`${API}/jobs`, { headers: authHdr() }),
        axios.get(`${API}/candidates`, { headers: authHdr() }),
        axios.get(`${API}/transactions`, { headers: authHdr() }),
        axios.get(`${API}/reviews`, { headers: authHdr() }),
        axios.get(`${API}/complaints`, { headers: authHdr() }),
        axios.get(`${API}/activity`, { headers: authHdr() }),
        axios.get(`${API}/analytics`, { headers: authHdr() }),
        axios.get(`${API}/fraud-reports`, { headers: authHdr() }),
        axios.get(`${API}/contact-messages`, { headers: authHdr() }),
        axios.get(`${HRTALK_ADMIN_API}/overview`, { headers: authHdr() }),
        axios.get(`${HRTALK_ADMIN_API}/experts`, { headers: authHdr() }),
        axios.get(`${HRTALK_ADMIN_API}/plans`, { headers: authHdr() }),
        axios.get(`${HRTALK_ADMIN_API}/slots`, { headers: authHdr() }),
        axios.get(`${API}/courses`, { headers: authHdr() }),
        axios.get(`${API}/course-instructors`, { headers: authHdr() }),
      ]);
      if (sR.status === "fulfilled") setStats(sR.value.data || {});
      if (rR.status === "fulfilled") setRecruiters(rR.value.data || []);
      if (jR.status === "fulfilled") setJobs(jR.value.data || []);
      if (cR.status === "fulfilled") setCandidates(cR.value.data || []);
      if (tR.status === "fulfilled") setTransactions(tR.value.data || []);
      if (revR.status === "fulfilled") {
        const payload = revR.value.data || {};
        setReviews(payload.reviews || []);
        setReviewSummary(payload.summary || { total: 0, pending: 0, visible: 0, hidden: 0, removal_requested: 0 });
      }
      if (compR.status === "fulfilled") {
        const payload = compR.value.data || {};
        setComplaints(payload.complaints || []);
        setComplaintSummary(payload.summary || { total: 0, open: 0, in_review: 0, resolved: 0, hidden: 0 });
      }
      if (actR.status === "fulfilled") {
        const payload = actR.value.data || {};
        setActivity(payload.items || []);
        setActivityCounts(payload.counts || {});
      }
      if (anR.status === "fulfilled") {
        setAnalytics(anR.value.data || {});
      }
      if (fR.status === "fulfilled") setFraudReports(fR.value.data || []);
      if (mR.status === "fulfilled") setContactMessages(mR.value.data || []);
      if (hoR.status === "fulfilled") setHrtalkOverview(hoR.value.data || { summary: {}, today_candidates: [], daily: [] });
      if (heR.status === "fulfilled") setHrtalkExperts(heR.value.data?.experts || []);
      if (hpR.status === "fulfilled") setHrtalkPlans(hpR.value.data?.plans || []);
      if (hsR.status === "fulfilled") setHrtalkSlots(hsR.value.data?.slots || []);
      if (crR.status === "fulfilled") setAdminCourses(crR.value.data?.courses || []);
      if (insR.status === "fulfilled") setCourseInstructors(insR.value.data?.instructors || []);
    } catch { flash("Some data failed to load", "error"); }
    finally { setLoading(false); setRefreshing(false); }
  }, [flash]);

  useEffect(() => { if (!getToken()) { window.location.href = "/admin/login"; return; } load(); }, [load]);

  /* Recruiter actions */
  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/recruiters/${id}/status`, { status }, { headers: authHdr() });
      setRecruiters(rs => rs.map(r => r.id === id ? { ...r, status } : r));
      flash(`Recruiter ${status}`);
    } catch { flash("Failed to update status", "error"); }
  };


  const handleSubToggle = async (id, plan_type, is_active) => {
  try {
    await axios.patch(`${API}/recruiters/${id}/subscription-toggle`, { plan_type, is_active }, { headers: authHdr() });
    setRecruiters(rs => rs.map(r => {
      if (r.id !== id) return r;
      return { ...r, subscriptions: { ...r.subscriptions, [plan_type]: { ...r.subscriptions[plan_type], active: is_active } } };
    }));
    flash(`${plan_type.toUpperCase()} plan ${is_active ? "resumed" : "paused"}`);
  } catch { flash("Failed to toggle subscription", "error"); }
};
  const handleCandidateStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/candidates/${id}/status`, { status }, { headers: authHdr() });
      setCandidates(list => list.map(c => c.id === id ? { ...c, status } : c));
      if (viewCandidate?.id === id) setViewCandidate(prev => prev ? { ...prev, status } : prev);
      flash(`Candidate ${status === "active" ? "unblocked" : "blocked"} successfully`);
    } catch (err) {
      // This will show the REAL server error message
      const serverMsg = err?.response?.data?.error
        || err?.response?.data?.message
        || JSON.stringify(err?.response?.data)
        || "Unknown server error";
      console.error("Full error response:", err?.response?.data);
      flash(`Server error: ${serverMsg}`, "error");
    }
  };



  const handleReviewSave = async (review) => {
    try {
      await axios.patch(`${API}/reviews/${review.id}`, {
        admin_modified_text: review.admin_modified_text ?? review.comment ?? "",
        admin_modified_rating: review.admin_modified_rating ?? review.rating ?? 0,
        admin_note: review.admin_note ?? "",
        status: review.status || "visible",
      }, { headers: authHdr() });
      setReviews(prev => prev.map(r => r.id === review.id ? { ...r, ...review } : r));
      setViewReview(null);
      flash("Review updated");
      load();
    } catch {
      flash("Failed to update review", "error");
    }
  };

  const handleReviewDelete = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: authHdr() });
      setReviews(prev => prev.filter(r => r.id !== id));
      if (viewReview?.id === id) setViewReview(null);
      flash("Review deleted");
      load();
    } catch {
      flash("Failed to delete review", "error");
    }
  };

  const handleComplaintSave = async (complaint) => {
    try {
      await axios.patch(`${API}/complaints/${complaint.id}`, {
        title: complaint.title,
        category: complaint.category,
        message: complaint.message,
        status: complaint.status,
        visible_on_profile: complaint.visible_on_profile,
        hidden_reason: complaint.hidden_reason,
        admin_note: complaint.admin_note,
      }, { headers: authHdr() });
      setComplaints(prev => prev.map(c => c.id === complaint.id ? { ...c, ...complaint } : c));
      flash("Complaint updated");
      load();
    } catch {
      flash("Failed to update complaint", "error");
    }
  };

  const handleComplaintDelete = async (id) => {
    if (!window.confirm("Delete this complaint permanently?")) return;
    try {
      await axios.delete(`${API}/complaints/${id}`, { headers: authHdr() });
      setComplaints(prev => prev.filter(c => c.id !== id));
      flash("Complaint deleted");
      load();
    } catch {
      flash("Failed to delete complaint", "error");
    }
  };

  const handleComplaintCreate = async (payload) => {
    if (!payload?.candidate_id) {
      throw new Error("Candidate is required");
    }
    const formData = new FormData();
    formData.append("title", payload.title || "");
    formData.append("category", payload.category || "misconduct");
    formData.append("message", payload.message || "");
    formData.append("visible_on_profile", payload.visible_on_profile ? "1" : "0");
    formData.append("terms_accepted", payload.terms_accepted ? "true" : "false");
    if (payload.proof) formData.append("proof", payload.proof);
    await axios.post(`${API}/candidates/${payload.candidate_id}/complaints`, formData, {
      headers: { ...authHdr(), "Content-Type": "multipart/form-data" },
    });
  };

  /* Job actions */
  const handleUpdateJob = (id, fields) => {
    setJobs(js => js.map(j => j.id === id ? { ...j, ...fields } : j));
    if (viewJob?.id === id) setViewJob(v => ({ ...v, ...fields }));
    flash("Job updated");
  };

  const handleDeleteJob = async (id) => {

    if (!window.confirm("Delete this job permanently?")) return;
    try {
      await axios.delete(`${API}/jobs/${id}`, { headers: authHdr() });
      setJobs(js => js.filter(j => j.id !== id));
      if (viewJob?.id === id) setViewJob(null);
      flash("Job deleted");
      load();
    } catch {
      flash("Failed to delete job", "error");
    }
  };
  const handleContactRead = async (id) => {
    try {
      await axios.patch(`${API}/contact-messages/${id}/read`, {}, { headers: authHdr() });
      setContactMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
      flash("Message marked as read");
    } catch {
      flash("Failed to update message", "error");
    }
  };
  const logout = () => {
    ["th_token", "th_user", "token", "user"].forEach(k => localStorage.removeItem(k));
    window.location.href = "/";
  };

  const NAV = [
    { id: "dashboard", icon: "📊", label: "Dashboard" },
    { id: "complaints", icon: "🧾", label: "Complaints", badge: complaints.length },
    { id: "activity", icon: "🧭", label: "Activity", badge: activity.length },
    {
      id: "support", icon: "🎧", label: "Support",
      badge: contactMessages.filter(m => !m.is_read).length + fraudReports.filter(r => !r.status || r.status === "open").length
    },

    { id: "database", icon: "🗄️", label: "Database", badge: 0 },
    { id: "access", icon: "🔐", label: "Access Control", badge: 0 },
    { id: "recruiters", icon: "🤝", label: "Recruiters", badge: recruiters.length },
    { id: "jobs", icon: "🏢", label: "Jobs", badge: jobs.length },
    { id: "courses", icon: "🎓", label: "Courses", badge: adminCourses.length },
    { id: "candidates", icon: "👥", label: "Candidates", badge: candidates.length },
    { id: "reviews", icon: "⭐", label: "Reviews", badge: reviews.length },
    { id: "transactions", icon: "💳", label: "Transactions", badge: transactions.length },
    { id: "hrtalk", icon: "HRT", label: "HRTalk", badge: hrtalkOverview?.summary?.total_sessions || 0 },
    { id: "analytics", icon: "📈", label: "Analytics" },
  ];

  const PAGE_TITLE = {
    dashboard: "Dashboard",
    recruiters: "Recruiters",
    jobs: "Jobs",
    courses: "Courses",
    candidates: "Candidates",
    reviews: "Reviews",
    complaints: "Complaints",
    activity: "Activity",
    transactions: "Transactions",
    hrtalk: "HRTalk",
    analytics: "Analytics",
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "var(--bg)", flexDirection: "column", gap: 20 }}>
      <div style={{ width: 56, height: 56, border: "5px solid var(--border)", borderTopColor: "var(--brand)", borderRadius: "50%", animation: "spin .8s linear infinite" }} />
      <p style={{ fontWeight: 700, color: "var(--text-2)", fontSize: 15 }}>Loading Admin Panel…</p>
    </div>
  );

  return (
    <div className="app-root">
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "success" })} />

      {/* Modals */}
      {viewRecruiter && <RecruiterModal
        recruiter={viewRecruiter}
        onClose={() => setViewRecruiter(null)}
        onStatusChange={handleStatusChange}
        onSubToggle={handleSubToggle}
        onAssignPlan={r => { setViewRecruiter(null); setAssignPlan(r); }}
      />}
      {assignPlan && <AssignPlanModal recruiter={assignPlan} onClose={() => setAssignPlan(null)} onAssigned={() => { load(); flash("Plan assigned! Invoice created."); }} flash={flash} />}
      {viewJob && <JobModal job={viewJob} onClose={() => setViewJob(null)} onUpdateJob={handleUpdateJob} onDeleteJob={handleDeleteJob} />}
      {viewCandidate && <CandidateModal candidate={viewCandidate} onClose={() => setViewCandidate(null)} onStatusChange={handleCandidateStatusChange} />}
      {viewInvoice && <InvoiceModal txn={viewInvoice} onClose={() => setViewInvoice(null)} />}

      {/* Sidebar */}
      <aside className="sidebar" style={{ width: collapsed ? "var(--sidebar-w-sm)" : "var(--sidebar-w)" }}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon"><span>A</span></div>
          {!collapsed && <div><div className="sidebar-logo-name">TalentHive</div><div className="sidebar-logo-sub">ADMIN PANEL</div></div>}
        </div>
        <nav className="sidebar-nav">
          {NAV.map(n => (
            <div key={n.id} className={`ni ${page === n.id ? "active" : ""}`} onClick={() => setPage(n.id)} title={collapsed ? n.label : undefined}>
              <span className="ni-icon">{n.icon}</span>
              {!collapsed && <span className="ni-label">{n.label}</span>}
              {!collapsed && n.badge > 0 && <span className="ni-badge">{n.badge}</span>}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="ni" onClick={() => setCollapsed(s => !s)} title={collapsed ? "Expand" : "Collapse"}>
            <span className="ni-icon">{collapsed ? "→" : "←"}</span>
            {!collapsed && <span className="ni-label">Collapse</span>}
          </div>
          <div className="ni" onClick={logout}>
            <span className="ni-icon">🚪</span>
            {!collapsed && <span className="ni-label">Logout</span>}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className={`main ${collapsed ? "sidebar-collapsed" : ""}`}>
        <div className="topbar">
          <div>
            <div className="topbar-title">{PAGE_TITLE[page]}</div>
            <div className="topbar-date">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          </div>
          <div className="topbar-right">
            <button className="btn bo" onClick={load} disabled={refreshing}>
              <span style={{ display: "inline-block", animation: refreshing ? "spin .8s linear infinite" : "none" }}>🔄</span>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <div className="topbar-admin">
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--brand)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12 }}>A</div>
              <span>Admin</span>
            </div>
          </div>
        </div>

        <div className="page-content">
          {page === "database" && <DatabasePage flash={flash} />}
          {page === "access" && <AccessControlPage flash={flash} />}
          {page === "fraud" && <FraudReportsPage reports={fraudReports} flash={flash} />}
          {page === "support" && (
            <SupportPage
              fraudReports={fraudReports}
              contactMessages={contactMessages}
              onReadMessage={handleContactRead}
              flash={flash}
            />
          )}
          {page === "dashboard" && <DashboardPage stats={stats} recruiters={recruiters} jobs={jobs} transactions={transactions} complaints={complaints} complaintSummary={complaintSummary} activity={activity} activityCounts={activityCounts} analytics={analytics} onViewRecruiter={setViewRecruiter} onViewJob={setViewJob} />}
          {page === "recruiters" && <RecruitersPage recruiters={recruiters} onView={setViewRecruiter} onAssignPlan={setAssignPlan} onStatusChange={handleStatusChange} />}
          {page === "jobs" && <JobsPage jobs={jobs} recruiters={recruiters} onView={setViewJob} onUpdateJob={handleUpdateJob} onPosted={load} flash={flash} />}
          {page === "courses" && <CoursesAdminPage courses={adminCourses} instructors={courseInstructors} onReload={load} flash={flash} />}
          {page === "candidates" && <CandidatesPage candidates={candidates} onView={setViewCandidate} onStatusChange={handleCandidateStatusChange} />}
          {page === "reviews" && <ReviewsPage reviews={reviews} summary={reviewSummary} onView={setViewReview} onDelete={handleReviewDelete} onSave={handleReviewSave} />}
          {page === "complaints" && <ComplaintsPage complaints={complaints} summary={complaintSummary} candidates={candidates} recruiters={recruiters} onSave={handleComplaintSave} onDelete={handleComplaintDelete} onCreate={handleComplaintCreate} onRefresh={load} flash={flash} />}
          {page === "activity" && <ActivityPage items={activity} counts={activityCounts} />}
          {page === "transactions" && <TransactionsPage transactions={transactions} onViewInvoice={setViewInvoice} />}
          {page === "hrtalk" && <HRTalkAdminPage overview={hrtalkOverview} experts={hrtalkExperts} plans={hrtalkPlans} slots={hrtalkSlots} onReload={load} flash={flash} />}
          {page === "analytics" && <GraphAnalyticsPage stats={stats} recruiters={recruiters} jobs={jobs} transactions={transactions} candidates={candidates} analytics={analytics} />}
        </div>
      </div>
    </div>
  );
}

